import { z } from "zod";
import { sourceSimpleSchema, estSourceTransformee } from "./composition-affichage.js";
import { conditionSchema, profondeurDeclaree } from "./conditions.js";
import { couleurSchema } from "./couleurs.js";
import { vuesHtmlDeclareesSchema } from "./vue-html.js";
import { enTetesDeLaPorte } from "./jeux-partages.mjs";
import { PREFIXE_CHAMP_SOCLE } from "./familles.js";

import {
  customFieldOptionSchema,
  customFieldTypeSchema,
  estFamilleADecider,
  fieldReferenceSchema,
  verifierReferenceDeChamp,
} from "./custom-field.js";

/* -------------------------------------------------------------------------- */
/* Le fichier d'écrans d'une app : `taki.screens.json`                        */
/* -------------------------------------------------------------------------- */

/**
 * CE QUE CE FICHIER DÉCRIT. La STRUCTURE d'un écran d'app : ses PORTES (les
 * jeux de données du menu), les VUES qu'il monte, le DÉTAIL PAR VUE (quel champ
 * tient quel rôle, quelles colonnes, dans quel ordre) et les CARTES DE FICHE.
 * C'est le voisin de `taki.fields.json` : celui-là dit ce que les champs SONT,
 * celui-ci dit où ils s'affichent.
 *
 * POURQUOI UN SECOND FICHIER ET PAS UNE CLEF DE PLUS. Le fichier de champs
 * entre par JEU et jamais par porte ; celui-ci entre par ÉCRAN puis par porte.
 * Deux fichiers, ce sont deux empreintes : l'éditeur de champs et l'éditeur de
 * portes ne se disputent pas la même écriture, et un conflit sur l'un ne bloque
 * pas l'autre. Le prix assumé : les recoupements entre les deux (une clef de
 * champ citée par un rôle) sont une vérification INTER-fichiers, donc le
 * travail de `scripts/check-app-entities.mjs`, pas d'un `superRefine`.
 *
 * Les sources d'affichage peuvent composer des champs, leurs replis et leurs
 * initiales. Les filtres acceptent les conditions du catalogue ferme du socle.
 * Les effets, canvas et calculs propres a une app restent des fonctions nommees.
 *
 * QUI LE LIT. Le socle l'assemble en objets du contrat des vues
 * (`packages/ui/src/views/screens.ts`), et la CI le recoupe avec le fichier de
 * champs, la fiche d'app et le catalogue de vues réellement monté.
 */

/**
 * UN ID DE VUE, tel que le contrat des vues le pose : `<espace>.<vue>`.
 *
 * Seule la FORME est vérifiée ici. Le CATALOGUE (quelles vues `core.*` existent
 * vraiment, quels rôles le contrat connaît) vit dans `@taki/ui` : le recopier
 * ici en ferait un fait écrit deux fois. C'est la CI qui le LIT et le confronte,
 * exactement comme elle relit l'énum des types de champ.
 */
const vueIdSchema = z
  .string()
  .regex(
    /^[a-z][a-zA-Z0-9-]*\.[a-z][a-zA-Z0-9-]*$/,
    "un id de vue s'écrit <espace>.<vue> (ex. core.liste)",
  );

/**
 * UN NOM DE CHAMP, dans l'un des DEUX espaces, jamais mélangés :
 *  - sans préfixe : une clef déclarée dans le `taki.fields.json` de l'app ;
 *  - préfixé `socle:` : un champ que la FAMILLE porte nativement
 *    (`socle:name`, `socle:reference`, `socle:status`).
 *
 * Les deux sont recoupables par la CI, et le préfixe est ce qui dit lequel des
 * deux accès l'app doit fournir (le sac `customFields`, ou le champ natif).
 */
const NOM_DE_CHAMP_RE = /^(?:socle:)?[a-z][a-zA-Z0-9_]*$/;
const nomDeChampSchema = z
  .string()
  .regex(NOM_DE_CHAMP_RE, "un nom de champ est une clef déclarée, ou socle:<champ natif>");

/** Le préfixe des champs natifs d'une famille (défini avec les familles : `familles.ts`). */
export { PREFIXE_CHAMP_SOCLE };

/**
 * LE VERROU D'UN EN-TÊTE DE FICHE (chantier `etapes-verrou`,
 * `docs/socle/etapes-verrou.md`) : tant que la condition `si` est vraie sur la
 * fiche (la même condition que les tranches et les filtres, évaluée avec le
 * jour, l'org et moi), des valeurs de l'en-tête ne se choisissent plus et la
 * `raison` se dit au cadenas. `avant` : une valeur du champ, celles de rang
 * inférieur dans le catalogue ordonné sont verrouillées. `depuis` : une valeur
 * du champ, celle-ci et les suivantes le sont. Les deux s'excluent ; sans l'un
 * ni l'autre, toute la piste l'est. La valeur courante reste visible.
 */
const verrouDEnTeteSchema = z
  .object({
    si: conditionSchema,
    avant: z.string().min(1).max(64).optional(),
    depuis: z.string().min(1).max(64).optional(),
    raison: z.string().trim().min(1, "dis pourquoi l'en-tête est verrouillé").max(80, "une raison tient en quelques mots"),
  })
  .strict()
  .refine((verrou) => verrou.avant === undefined || verrou.depuis === undefined, {
    message: "un verrou ferme avant une valeur OU à partir d'une valeur, jamais les deux",
  });

/** Combien de verrous un seul en-tête peut porter. */
export const VERROUS_PAR_EN_TETE_MAX = 8;

/**
 * UN EN-TÊTE DE FICHE (`portes[].etapes`, chantier `fiche-suivi`) : un champ
 * liste du jeu de la porte et la façon de le montrer dans la rangée 3, avec des
 * verrous facultatifs : la clef `verrou` porte un objet seul (un verrou) ou une
 * liste (plusieurs, dans les deux sens). Le type lu est `EnTeteDeFiche`
 * (`jeux-partages.d.mts`), rendu par `enTetesDeLaPorte`, qui dit `verrous`.
 */
const enTeteDeFicheSchema = z
  .object({
    champ: nomDeChampSchema,
    mode: z.enum(["flux", "selection"]),
    verrou: z.union([verrouDEnTeteSchema, z.array(verrouDEnTeteSchema).min(1).max(VERROUS_PAR_EN_TETE_MAX)]).optional(),
  })
  .strict();

/**
 * UN NOM DE FONCTION du registre `FONCTIONS` de l'app. C'est un identifiant
 * JavaScript, jamais une expression : ni parenthèses, ni point, ni opérateur.
 */
const nomDeFonctionSchema = z
  .string()
  .regex(/^[A-Za-z_$][A-Za-z0-9_$]*$/, "un nom de fonction est un identifiant, jamais un appel");

/**
 * LES EFFETS TENUS PAR LE SOCLE, que la `fn` d'une action nomme avec le préfixe
 * `socle:`. Une LISTE FERMÉE, et c'est le fond : l'effet d'une app vit dans son
 * code, celui du socle vit dans `@taki/app-runtime` (`effets-socle.ts`), et une
 * ORG qui personnalise une app n'écrit aucun code. Sans ce préfixe, un bouton
 * « Générer maintenant » déclaré par l'org nommerait un effet que l'app ne
 * fournit pas, et l'écran refuserait de monter.
 *
 * Le runtime résout chacun de ces noms ; un nom `socle:` absent d'ici est
 * refusé au schéma, avec la liste de ceux qui existent.
 */
export const EFFETS_DU_SOCLE = ["socle:lancerRecurrences", "socle:notifier", "socle:exporterPdf"] as const;
export type EffetDuSocle = (typeof EFFETS_DU_SOCLE)[number];

/**
 * LES EFFETS DE FICHE : ceux qui portent sur LA fiche ouverte, et sur rien
 * d'autre. La rangée 3a appartient au niveau COLLECTION et ne se rend pas sur
 * une fiche : une action qui nomme l'un d'eux n'y apparaît donc pas, elle
 * prend place dans le menu « ... » de la fiche ouverte (`@taki/app-runtime`,
 * `gestes-de-fiche.ts`). `socle:exporterPdf` rend la fiche en PDF, à la marque
 * de l'org, et la range dans ses pièces jointes (`docs/PDF.md`).
 */
export const EFFETS_DE_FICHE = ["socle:exporterPdf"] as const satisfies readonly EffetDuSocle[];
export type EffetDeFiche = (typeof EFFETS_DE_FICHE)[number];

/** Cet effet porte-t-il sur la fiche ouverte (menu « ... » de la fiche, pas rangée 3a) ? */
export function estEffetDeFiche(nom: string | undefined): nom is EffetDeFiche {
  return nom !== undefined && (EFFETS_DE_FICHE as readonly string[]).includes(nom);
}

/** Le préfixe d'un effet d'action tenu par le socle. */
export const PREFIXE_EFFET_SOCLE = "socle:";

/**
 * LES JETONS DU SOCLE, qu'une valeur de `creation.defauts` nomme au lieu d'une
 * valeur fixe. Une LISTE FERMÉE, comme les effets : le jeton se RÉSOUT à
 * l'ouverture du brouillon, donc c'est un calcul que seul le socle sait faire,
 * et que la donnée se contente de nommer. Trois, chacun pour une nature de
 * champ :
 * - `socle:personneConnectee` devient le `personId` de qui ouvre la fiche, pour
 *   un champ `personne`. Pas de jeton e-mail ni uid : ce serait rouvrir le
 *   texte libre que le champ `personne` ferme.
 * - `socle:aujourdhui` devient le JOUR d'alors, date pure `YYYY-MM-DD` dans le
 *   fuseau de l'org, pour un champ déclaré `date`.
 * - `socle:maintenant` devient l'INSTANT d'alors, pour un champ déclaré
 *   `datetime` (chaîne ISO dans le sac) ou un champ natif de la famille de
 *   genre date (un `Date` à la racine).
 * Ce qui range le jeton au bon champ est contrôlé hors de ce schéma, par
 * `pnpm apps:check` (le schéma ne voit pas les champs du jeu).
 */
export const JETONS_DU_SOCLE = ["socle:personneConnectee", "socle:aujourdhui", "socle:maintenant"] as const;
export type JetonDuSocle = (typeof JETONS_DU_SOCLE)[number];

/** Le préfixe d'un jeton du socle, le même que celui des effets. */
export const PREFIXE_JETON_SOCLE = "socle:";

/** La valeur est-elle un jeton du socle connu ? */
export function estJetonDuSocle(valeur: unknown): valeur is JetonDuSocle {
  return typeof valeur === "string" && (JETONS_DU_SOCLE as readonly string[]).includes(valeur);
}

/**
 * Une valeur de départ de la fiche vide : une chaîne, un nombre, un booléen,
 * ou un jeton du socle. Une chaîne qui COMMENCE par `socle:` sans être un
 * jeton connu est une faute (une coquille resterait sinon écrite telle quelle
 * dans la fiche), refusée avec la liste de ceux qui existent.
 */
const valeurDeDefautSchema = z.union([
  z.string().superRefine((valeur, ctx) => {
    if (valeur.startsWith(PREFIXE_JETON_SOCLE) && !estJetonDuSocle(valeur)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `"${valeur}" n'est pas un jeton du socle (connus : ${JETONS_DU_SOCLE.join(", ")})`,
      });
    }
  }),
  z.number(),
  z.boolean(),
]);

/** L'effet d'une action : un nom du registre de l'app, ou un effet du socle. */
const nomDEffetSchema = z.string().superRefine((nom, ctx) => {
  if (nom.startsWith(PREFIXE_EFFET_SOCLE)) {
    if (!(EFFETS_DU_SOCLE as readonly string[]).includes(nom)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `l'effet "${nom}" n'est pas un effet du socle (connus : ${EFFETS_DU_SOCLE.join(", ")})`,
      });
    }
    return;
  }
  if (!/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(nom)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "un nom de fonction est un identifiant, jamais un appel",
    });
  }
});

/* -------------------------------------------------------------------------- */
/* La brique du bas : d'où sort la valeur d'un rôle                           */
/* -------------------------------------------------------------------------- */

/**
 * LA SOURCE D'UN ROLE : champ, champs, fn ou le titre pour ses initiales.
 * Une origine exclusive. `champs` joint les parties non vides dans l'ordre,
 * avec separateur, replis et transformations fermes. Rien ne s'interpole.
 * Les calculs propres a l'app restent des fonctions nommees.
 */
export const appScreenSourceSchema = z.unknown().superRefine((value, ctx) => {
  if (!profondeurDeclaree(value)) ctx.addIssue({ code: "custom", message: "composition trop profonde" });
}).pipe(z
  .object({
    /** UN champ, déclaré ou natif. */
    champ: nomDeChampSchema.optional(),
    /** PLUSIEURS champs, joints par le socle dans l'ordre donné. */
    champs: z.array(z.union([nomDeChampSchema, sourceSimpleSchema])).min(2).max(32).optional(),
    role: z.literal("title").optional(),
    separateur: z.string().max(80).optional(),
    replis: z.array(sourceSimpleSchema).min(1).max(16).optional(),
    affichage: z.literal("libelle").optional(),
    transformation: z.literal("initiales").optional(),
    /** Une fonction du registre de l'app, quand la valeur est un calcul. */
    fn: nomDeFonctionSchema.optional(),
    /** Le texte affiché quand la valeur est vide ("Référence non renseignée"). */
    vide: z.string().min(1).optional(),
    /**
     * L'EXEMPLE GRIS de la case vide, sur la fiche de CREATION. A ne pas
     * confondre avec `vide` juste au-dessus : `vide` est ce qu'une fiche
     * EXISTANTE affiche quand personne n'a rempli la case ("Reference non
     * renseignee") ; `placeholder` est ce qu'une fiche PAS ENCORE CREEE montre
     * dans la case a remplir ("Sony FX9 n°3"). Les deux textes ne disent pas la
     * meme chose et ne s'ecrivent pas au meme moment.
     *
     * LU SUR LE ROLE `title` seulement (le titre du brouillon), TOLERE ailleurs :
     * les autres roles se rendent par leurs colonnes, qui portent deja le leur.
     */
    placeholder: z.string().min(1).optional(),
    /**
     * Le CATALOGUE du rôle, nommé : une fonction qui rend les options au
     * RUNTIME. Obligatoire pour `status`, `groupKey` et `lane`, sans quoi le
     * kanban fabrique des colonnes fantômes (le socle le vérifie).
     */
    catalogue: nomDeFonctionSchema.optional(),
    /** L'habillage, nommé : ce qui s'AFFICHE à la place de la valeur brute. */
    habillage: nomDeFonctionSchema.optional(),
    /**
     * Le rôle s'ÉCRIT. C'est un booléen, pas un gestionnaire : l'écriture vient
     * de l'accès au jeu que l'app fournit. Sur un `groupKey` de kanban, c'est
     * lui qui rend la carte déplaçable.
     */
    modifiable: z.boolean().optional(),
  })
  .strict()
  .superRefine((source, ctx) => {
    const posees = [source.champ, source.champs, source.fn, source.role].filter((v) => v !== undefined);
    if (posees.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "une source porte exactement une origine : champ, champs, fn ou role",
      });
    }
    if (source.modifiable && (estSourceTransformee(source) || source.role !== undefined)) ctx.addIssue({ code: "custom", message: "une composition ne se modifie pas comme un champ" });
    if (source.fn && (estSourceTransformee(source) || source.separateur !== undefined)) ctx.addIssue({ code: "custom", message: "fn ne porte pas une composition" });
    if (source.role && source.transformation !== "initiales") ctx.addIssue({ code: "custom", message: "le titre derive fournit les initiales" });
    if (source.role && [source.separateur, source.replis, source.affichage, source.catalogue, source.habillage, source.placeholder].some(v => v !== undefined))
      ctx.addIssue({ code: "custom", message: "le titre derive ne porte que transformation et vide" });
  }));

/** D'où sort la valeur d'un rôle. */
export type AppScreenSource = z.infer<typeof appScreenSourceSchema>;

/* -------------------------------------------------------------------------- */
/* Les colonnes de l'écran                                                    */
/* -------------------------------------------------------------------------- */

/**
 * LES TROIS AGRÉGATS QUE LE SOCLE SAIT FAIRE, en une table NOMMÉE et déclarée
 * une seule fois : le pied d'une colonne de tableau (`total` sur une colonne) et
 * le CHAMP DE TOTAL d'une carte de fiche (`total` sur une entrée de carte)
 * comptent exactement la même chose. Les écrire deux fois en ferait deux tables
 * qui divergeront, et la CI relit celle-ci plutôt que de la recopier.
 */
const agregatSchema = z.enum(["somme", "moyenne", "compte"]);

/**
 * UNE COLONNE DU CATALOGUE DE L'ÉCRAN, déclarée UNE fois.
 *
 * Pourquoi le catalogue est à l'ÉCRAN et l'ORDRE à la vue : le tableau et la
 * fiche d'un même écran montrent les mêmes colonnes, dans deux ordres et sur
 * deux longueurs. Répéter l'habillage et l'agrégat dans chaque vue en ferait
 * deux faits qui divergeront. Une vue ne déclare donc que la LISTE ORDONNÉE des
 * noms qu'elle reprend.
 *
 * `label` et `type` ne se déclarent QUE pour un champ natif (`socle:`) : pour
 * un champ déclaré, ils vivent déjà dans `taki.fields.json`, et les réécrire
 * ici serait le même fait à deux endroits.
 */
export const appScreenColumnSchema = z
  .object({
    champ: nomDeChampSchema,
    /** Le libellé d'en-tête. OBLIGATOIRE pour un champ natif, INTERDIT sinon. */
    label: z.string().min(1).optional(),
    /** Le type de saisie. OBLIGATOIRE pour un champ natif, INTERDIT sinon. */
    type: customFieldTypeSchema.optional(),
    /** Colonne triable au clic. Absent : le défaut du socle (tout sauf un texte long). */
    triable: z.boolean().optional(),
    /** L'agrégat de pied de colonne, NOMMÉ. Le socle le calcule, le fichier le nomme. */
    total: agregatSchema.optional(),
    /** L'habillage, nommé. Le formatage ne se déclare pas, il se nomme. */
    habillage: nomDeFonctionSchema.optional(),
    /**
     * LE NOMBRE DE DÉCIMALES d'une colonne NATIVE de type `number`, à côté de
     * son `habillage` : `socle:basePrice` habillé `euros` avec `decimales: 2`
     * s'écrit « 12,00 € » même rond. Même règle que `label`, `type` et
     * `options` : un champ natif n'a aucune ligne dans `taki.fields.json` où
     * poser ce réglage, un champ déclaré le porte déjà (`decimales` de son
     * champ). Voir `docs/socle/montant-decimales.md`.
     */
    decimales: z.number().int().min(0).max(6).optional(),
    /**
     * L'EXEMPLE GRIS de la case vide sur la fiche de CREATION, et dans
     * l'editeur ("SNY-FX9-003"). Il se declare ICI pour un champ NATIF
     * (`socle:`), qui n'a pas de ligne dans `taki.fields.json` ou le poser ; sur
     * un champ declare, il surcharge celui du fichier de champs pour cet ecran.
     */
    placeholder: z.string().min(1).optional(),
    /**
     * LE CATALOGUE DE VALEURS D'UN CHAMP NATIF A CHOIX, et lui seul.
     *
     * Un champ DÉCLARÉ porte ses options dans `taki.fields.json` ; un champ
     * NATIF n'y a aucune ligne, donc rien ne portait ses valeurs, et la pastille
     * d'un `socle:status` ne pouvait venir que d'une fonction nommée
     * (`catalogue`), écrite dans le TypeScript de l'app. Une app née du Studio
     * n'écrit pas ce TypeScript : son statut restait donc muet.
     *
     * Même règle que `label` et `type`, pour la même raison : ce qui n'a pas
     * d'autre porteur se déclare ici, ce qui en a un n'y est pas répété.
     */
    options: z.array(customFieldOptionSchema).min(1).optional(),
    /**
     * LA CASE NE SE VIDE PAS : elle se corrige. Réservé à un champ NATIF, pour
     * la même raison que `label` et `type` : un champ déclaré porte déjà son
     * `required` dans `taki.fields.json`.
     *
     * POURQUOI ICI ET PAS DANS LE SCHÉMA DE LA FAMILLE. Le schéma dit ce qui est
     * VALIDE pour toutes les apps (`name` peut manquer sur une personne) ; cette
     * clef dit ce que CET écran exige (la raison sociale d'une société est son
     * titre, la vider laisserait des lignes muettes). L'écran générique refuse
     * la case vidée avec une phrase lisible, avant le SDK.
     */
    requis: z.boolean().optional(),
    /**
     * LA CIBLE D'UNE COLONNE NATIVE DE TYPE `lien` (`socle:contactId` sur un
     * projet désigne un contact), et elle seule. Même règle que `label`, `type`
     * et `options` : un champ natif n'a aucune ligne dans `taki.fields.json`,
     * donc c'est ici, et seulement ici, qu'il peut dire de quelle famille vient
     * la fiche dont il porte l'id. Un champ déclaré porte sa cible dans le
     * fichier de champs, à côté de son type.
     *
     * C'EST CE QUI REND LES APPS CONNECTABLES SANS PORTE : la colonne nomme la
     * famille, le socle en résout le nom (annuaire) et sait l'ouvrir dans l'app
     * qui la montre. Aucune porte du même écran n'a plus à la porter pour ça.
     */
    reference: fieldReferenceSchema.optional(),
  })
  .strict()
  .superRefine((colonne, ctx) =>
    verifierReferenceDeChamp(colonne, `la colonne "${colonne.champ}"`, ctx),
  );

/** Une colonne du catalogue d'un écran. */
export type AppScreenColumn = z.infer<typeof appScreenColumnSchema>;

/* -------------------------------------------------------------------------- */
/* Les cartes de la fiche                                                     */
/* -------------------------------------------------------------------------- */

/** L'id d'une carte de fiche : en minuscules, tirets admis, comme un id de porte. */
export const idDeCarteSchema = z
  .string()
  .regex(/^[a-z][a-z0-9-]*$/, "un id de carte est en minuscules, tirets admis");

/**
 * UNE ENTRÉE DE CARTE DE FICHE : un nom de champ, ou ce nom plus son VERROU.
 *
 * LE DÉFAUT EST « MODIFIABLE », et c'est la décision du 04/09/2026 : une fiche
 * est le SEUL endroit où l'on corrige une valeur, donc une case qui ne s'édite
 * pas doit être un choix ÉCRIT, jamais un silence. `"kilometrage"` s'édite en
 * place ; `{ "champ": "kilometrage", "modifiable": false }` est verrouillée.
 *
 * ÉCRIRE `modifiable: true` EST DONC INUTILE et reste permis : c'est le défaut
 * dit à voix haute. Le contrat complet vit dans `docs/VUES-CONTRAT.md`.
 */
/**
 * LA PONDÉRATION D'UN CHAMP DE TOTAL : « quantité x prix de l'article ».
 *
 * Un total simple additionne UN champ des fiches liées. Il lui manque le cas où
 * le montant d'une ligne n'est pas sur la ligne mais sur la fiche qu'elle
 * DÉSIGNE : la ligne d'un protocole porte une quantité et l'id d'un article, le
 * prix est sur l'article. La pondération dit ce qui multiplie la valeur de
 * `champ` (le champ du total) : le nombre que porte la fiche désignée.
 *
 * `par` EST UN CHAMP DE RÉFÉRENCE DU JEU LIÉ (type `lien` dans son
 * `taki.fields.json`) : c'est sa déclaration qui dit QUEL jeu il désigne, la
 * pondération ne le répète pas. `champ` est un champ numérique de CE jeu désigné
 * (déclaré chez lui, ou natif avec le préfixe `socle:`).
 *
 * LA RÈGLE DE LA LIGNE QUI NE COMPTE PAS, écrite ici une fois : une ligne sans
 * valeur, sans fiche désignée, désignant une fiche introuvable ou supprimée, ou
 * dont la fiche désignée n'a pas le nombre, ne compte pas. Elle n'est ni zéro ni
 * une erreur : elle est absente de la somme, comme une case vide l'est d'une
 * somme simple.
 *
 * UN SEUL MÉCANISME : la pondération n'existe que sur le champ de total d'une
 * carte de fiche, elle n'ouvre ni une famille d'agrégats ni une autre entrée.
 */
export const appScreenPonderationSchema = z
  .object({
    /** Le champ de RÉFÉRENCE du jeu lié (`articleId`) : il désigne la fiche qui porte le nombre. */
    par: nomDeChampSchema,
    /** Le champ numérique de la fiche DÉSIGNÉE (`priceInclTax`). */
    champ: nomDeChampSchema,
  })
  .strict();

/** La pondération d'un champ de total. */
export type AppScreenPonderation = z.infer<typeof appScreenPonderationSchema>;

/**
 * UN CHAMP DE TOTAL (rollup) : une case de carte qui ne lit AUCUN champ de la
 * fiche, mais COMPTE les fiches liées (« Interventions : 12 », « Heures : 37 »).
 *
 * IL NE SE CALCULE PAS SUR CE QU'ON A EN MÉMOIRE, et c'est toute la raison de
 * sa forme : le nombre se demande au SERVEUR sur le jeu lié, comme un `count()`
 * (AGENTS.md section 7). Le fichier NOMME donc l'agrégat et le champ qui le
 * porte ; il n'écrit ni requête, ni condition.
 *
 * `fois` PONDÈRE UNE SOMME (« Prix du protocole » = somme des quantités des
 * lignes, chacune multipliée par le prix de l'article que la ligne désigne) :
 * voir `appScreenPonderationSchema`. Sans `fois`, rien ne change.
 *
 * `libelle` EST OBLIGATOIRE et il est ici, pas ailleurs : ce total n'est le
 * champ d'aucun jeu, donc aucun `taki.fields.json` ne connaît son mot.
 *
 * IL N'EST JAMAIS MODIFIABLE : il n'y a rien à écrire dans un compte. La clef
 * `modifiable` est refusée par `.strict()` plutôt que d'être acceptée puis
 * ignorée.
 */
export const appScreenRollupFieldSchema = z
  .object({
    /** Le mot devant la valeur. Un libellé, une ligne (règle G26). */
    libelle: z.string().min(1),
    /** L'id d'un LIEN de la porte qui porte cette fiche. */
    liee: z.string().min(1),
    total: agregatSchema,
    /** Le champ du jeu LIÉ sur lequel le calcul porte. Absent seulement pour `compte`. */
    champ: nomDeChampSchema.optional(),
    /** La pondération : chaque valeur de `champ` est multipliée par un nombre lu sur la fiche que la ligne DÉSIGNE. */
    fois: appScreenPonderationSchema.optional(),
  })
  .strict()
  .superRefine((entree, ctx) => {
    const faute = (message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    };
    /* La MÊME règle que les stats, dans les mêmes mots : compter des fiches ne
       porte aucun champ, sommer ou moyenner en exige un. */
    if (entree.total === "compte") {
      if (entree.champ !== undefined) {
        faute('le calcul "compte" compte les fiches : il ne porte aucun champ');
      }
    } else if (entree.champ === undefined) {
      faute(`le calcul "${entree.total}" porte sur un champ : il en manque un`);
    }
    /* UNE MOYENNE PONDÉRÉE a deux sens (diviser par le nombre de lignes, ou par
       la somme des poids) : le socle n'en choisit pas, la pondération ne se
       déclare que sur une somme. */
    if (entree.fois !== undefined && entree.total !== "somme") {
      faute(`la pondération "fois" ne vaut que pour le calcul "somme" (reçu : "${entree.total}")`);
    }
  });

/** Un champ de total sur les fiches liées. */
export type AppScreenRollupField = z.infer<typeof appScreenRollupFieldSchema>;

/*
 * L'ORDRE DES TROIS FORMES N'EST PAS INDIFFÉRENT, et c'est un détail de Zod qui
 * se paie en messages : quand aucune forme ne convient, l'union rend l'erreur de
 * la PREMIÈRE qui a presque marché. Le champ de total passe donc AVANT le nom
 * verrouillé, sans quoi « le calcul "compte" ne porte aucun champ » serait
 * remplacé par un « clef inconnue "libelle" » qui ne dit rien de la faute.
 */
export const appScreenCardFieldSchema = z.union(
  [
    nomDeChampSchema,
    appScreenRollupFieldSchema,
    z
      .object({
        champ: nomDeChampSchema,
        modifiable: z.boolean().optional(),
      })
      .strict(),
  ],
  {
    errorMap: () => ({
      message:
        "une entrée de carte est un nom de champ, { champ, modifiable }, " +
        "ou un total { libelle, liee, total }",
    }),
  },
);

/** Une entrée de carte de fiche : le nom nu, le nom et son verrou, ou un total. */
export type AppScreenCardField = z.infer<typeof appScreenCardFieldSchema>;

/** L'entrée est-elle un CHAMP DE TOTAL plutôt qu'un champ de la fiche ? */
export const estChampDeTotal = (entree: AppScreenCardField): entree is AppScreenRollupField =>
  typeof entree !== "string" && "total" in entree;

/**
 * LE NOM NU D'UNE ENTRÉE DE CARTE, ou `undefined` quand l'entrée ne nomme aucun
 * champ de CETTE fiche.
 *
 * Le cas est celui du champ de total : son `champ` appartient au jeu LIÉ, et le
 * rendre ici le ferait chercher dans les colonnes de la vue courante, où il n'a
 * aucune raison d'être.
 */
export const nomDeChampDeCarte = (entree: AppScreenCardField): string | undefined => {
  if (typeof entree === "string") return entree;
  return estChampDeTotal(entree) ? undefined : entree.champ;
};

/**
 * UNE CARTE DE CHAMPS : un titre et les champs qu'elle regroupe.
 *
 * LA POSITION SUIT LE RANG : le socle fait couler les cartes sur DEUX colonnes
 * dans l'ordre de déclaration, et une carte qui le demande prend sa rangée
 * entière. Ajouter une carte, c'est donc un objet de plus dans ce tableau, et
 * son rang décide de sa place. UNE SEULE EXCEPTION, `placement: "droite"`
 * (décision du fondateur du 14/10/2026) : voir plus bas.
 */
export const appScreenFieldsCardSchema = z
  .object({
    /**
     * L'ID D'UNE CARTE, facultatif, et ce qu'il achète : la FUSION. Une carte
     * sans id se reconnaît à son rang, donc une carte insérée par le client
     * décale toutes celles du modèle ; une carte avec id se suit d'une version
     * du modèle à l'autre (`fusionnerDeclarations`). Le Studio en pose un à
     * chaque carte qu'il écrit ; un fichier écrit à la main peut s'en passer.
     */
    id: idDeCarteSchema.optional(),
    titre: z.string().min(1),
    champs: z.array(appScreenCardFieldSchema).min(1),
    /**
     * LA DISPOSITION DE LA CARTE : comment elle occupe la rangée, jamais OÙ
     * elle se pose (le socle continue de faire couler les cartes dans l'ordre
     * de déclaration).
     *
     *   `demi`         la carte tient dans UNE des deux colonnes du plan ;
     *   `pleine`       la rangée entière, champs empilés et rendus en PROSE
     *                  (la carte Notes : un texte long ne se lit pas en
     *                  demi-colonne) ;
     *   `titre-pleine` le TITRE prend la rangée, les champs se répartissent sur
     *                  les deux colonnes du plan, et `champs` se lit de HAUT
     *                  EN BAS, colonne par colonne : la première moitié à
     *                  gauche (le champ de plus quand le compte est impair),
     *                  la suite à droite. On l'écrit dans l'ordre où on le lit.
     *
     * ABSENTE VAUT `demi`, et écrire `"demi"` est permis : c'est le défaut dit
     * à voix haute, comme `modifiable: true`. La clef `large` d'avant ce
     * chantier N'EXISTE PLUS et n'a AUCUN alias (`.strict()` la refuse) : un
     * fait, une écriture.
     */
    disposition: z.enum(["demi", "pleine", "titre-pleine"]).optional(),
    /**
     * LE PLACEMENT DE LA CARTE : dans le flot (`principal`, le défaut) ou dans
     * la COLONNE DE DROITE, en haut de la fiche (`droite`, comme la
     * Description de la fiche To do).
     *
     * Dès qu'une carte est à droite, la fiche se range en deux temps : en HAUT,
     * deux colonnes, les cartes de champs en demi à gauche (empilées, dans leur
     * ordre) et les cartes à droite (empilées, dans leur ordre) ; DESSOUS, les
     * cartes qui prennent la rangée (pleine, titre-pleine, liées, fichiers),
     * dans leur ordre. Sous 960px, tout se lit sur une colonne, dans le même
     * ordre : la gauche, puis la droite, puis le dessous.
     *
     * UNE CARTE À DROITE EST EN DEMI : la colonne de droite n'a pas de rangée
     * entière à offrir, donc `droite` avec `pleine` ou `titre-pleine` est refusé
     * (un fait, une écriture). ABSENT VAUT `principal`, et l'écrire est permis.
     */
    placement: z.enum(["principal", "droite"]).optional(),
  })
  .strict()
  .superRefine((carte, ctx) => {
    if (
      carte.placement === "droite" &&
      (carte.disposition === "pleine" || carte.disposition === "titre-pleine")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["placement"],
        message:
          `la carte "${carte.titre}" est à droite : elle tient en demi, ` +
          `la disposition "${carte.disposition}" n'y a pas de sens`,
      });
    }
  });

/**
 * UNE CARTE LIÉE : une carte qui montre les FICHES LIÉES, par une vue du socle.
 *
 * ELLE NE DÉCRIT AUCUN RENDU. Elle nomme un LIEN de la porte, la VUE qui rend
 * les fiches liées, et le plafond de ce qu'elle en charge. La liste et le
 * tableau du socle font le reste : une carte liée n'est pas une sixième vue,
 * c'est l'une des deux, montée dans une carte.
 *
 * DEUX VUES ET PAS PLUS, et c'est une table fermée : un kanban ou un calendrier
 * dans une carte de fiche demanderait une hauteur et un défilement que la carte
 * n'a pas. La vue citée doit être MONTÉE par l'écran (vérifié plus bas) : la
 * carte rend une vue du catalogue, elle n'en ouvre pas une de plus.
 *
 * ELLE EST TOUJOURS PLEINE LARGEUR : une liste de fiches en demi-colonne ne se
 * lit pas. `disposition` n'est donc pas une clef d'ici, `.strict()` la refuse.
 */
export const appScreenLinkedCardSchema = z
  .object({
    /** L'id de la carte, facultatif : voir `appScreenFieldsCardSchema.id`. */
    id: idDeCarteSchema.optional(),
    titre: z.string().min(1),
    /** L'id d'un LIEN de la porte qui porte cette fiche. */
    liee: z.string().min(1),
    vue: z.enum(["core.liste", "core.tableau"]),
    /**
     * COMBIEN DE FICHES LIÉES LA CARTE CHARGE, au plus. Absent : le défaut du
     * socle, posé à l'ASSEMBLAGE et pas ici (un défaut écrit dans le schéma
     * s'écrirait dans tous les fichiers, et deviendrait un fait de plus à tenir).
     */
    plafond: z.number().int().min(1).max(500).optional(),
  })
  .strict();

/**
 * UNE CARTE DE FICHIERS : la section des fichiers joints de la fiche, posée
 * parmi les autres cartes, à son rang.
 *
 * ELLE NE MONTRE RIEN SI LE JEU N'OUVRE PAS LES FICHIERS (`jeux[].fichiers`
 * de `taki.fields.json`) : la capacité est un fait des DONNÉES, cette carte n'en
 * est que la présentation. Couper la capacité fait donc disparaître la carte,
 * sans la réécrire. `modifiable: false` la met en lecture seule (on télécharge,
 * on n'ajoute ni ne retire), toujours bornée par les droits de la porte ; nu,
 * elle suit les droits. Toujours pleine largeur, comme une carte liée.
 */
export const appScreenFilesCardSchema = z
  .object({
    /** L'id de la carte, facultatif : voir `appScreenFieldsCardSchema.id`. */
    id: idDeCarteSchema.optional(),
    titre: z.string().min(1),
    fichiers: z.literal(true),
    modifiable: z.literal(false).optional(),
  })
  .strict();

/**
 * UNE CARTE DE FICHE : une carte de CHAMPS, une carte LIÉE, ou la carte des
 * FICHIERS joints.
 *
 * LES DEUX FORMES SONT EXCLUSIVES, et c'est ce que l'union dit : une carte
 * montre les champs de LA fiche (`champs`), ou les fiches d'un AUTRE jeu
 * (`liee` + `vue`). Mélanger les deux dans un seul objet demanderait de choisir
 * une disposition, un ordre et un verrou pour un contenu qui n'en a pas.
 */
export const appScreenCardSchema = z.union(
  [appScreenFieldsCardSchema, appScreenLinkedCardSchema, appScreenFilesCardSchema],
  {
    errorMap: () => ({
      message:
        "une carte montre des champs (champs), les fiches liées (liee, vue) ou les " +
        "fichiers joints (fichiers: true) : aucune des trois ici",
    }),
  },
);

/** Une carte de fiche. */
export type AppScreenCard = z.infer<typeof appScreenCardSchema>;

/** Une carte de champs. */
export type AppScreenFieldsCard = z.infer<typeof appScreenFieldsCardSchema>;

/** Une carte liée. */
export type AppScreenLinkedCard = z.infer<typeof appScreenLinkedCardSchema>;

/** Une carte des fichiers joints. */
export type AppScreenFilesCard = z.infer<typeof appScreenFilesCardSchema>;

/** La carte montre-t-elle des fiches LIÉES plutôt que des champs ? */
export const estCarteLiee = (carte: AppScreenCard): carte is AppScreenLinkedCard =>
  "liee" in carte;

/** La carte est-elle celle des FICHIERS joints ? */
export const estCarteFichiers = (carte: AppScreenCard): carte is AppScreenFilesCard =>
  "fichiers" in carte;

/** La carte montre-t-elle les CHAMPS de la fiche (ni liée, ni fichiers) ? */
export const estCarteDeChamps = (carte: AppScreenCard): carte is AppScreenFieldsCard =>
  "champs" in carte;

/* -------------------------------------------------------------------------- */
/* La surbrillance d'une carte de kanban                                      */
/* -------------------------------------------------------------------------- */

/**
 * UNE RÈGLE DE SURBRILLANCE (`docs/socle/cartes-kanban.md`, section 2) : quand
 * `si` est vrai, la carte prend la bordure et l'ombre de `couleur`, et pose
 * son `etiquette` en tête du bas de carte.
 *
 * `si` est une condition du socle (la même que les filtres et la tranche), ou
 * le mot `"nouveau"` : la fiche jamais ouverte sur ce poste. « Nouveau » n'est
 * donc pas un cas à part, c'est une règle comme une autre ; un kanban qui n'en
 * déclare aucune reçoit celle-là, et celle-là seulement (`@taki/ui`,
 * `SURBRILLANCE_PAR_DEFAUT`).
 *
 * L'étiquette est BORNÉE (24 caractères) : elle tient dans une pastille du bas
 * de carte, qui ne passe jamais à la ligne.
 */
export const appScreenHighlightSchema = z
  .object({
    si: z.union([z.literal("nouveau"), conditionSchema]),
    couleur: couleurSchema,
    etiquette: z.string().min(1).max(24).optional(),
  })
  .strict();

/** Une règle de surbrillance d'une carte de kanban. */
export type AppScreenHighlight = z.infer<typeof appScreenHighlightSchema>;

/* -------------------------------------------------------------------------- */
/* Le détail par vue                                                          */
/* -------------------------------------------------------------------------- */

/**
 * LE DÉTAIL D'UNE VUE : quel champ tient quel rôle, quelles colonnes, quelles
 * cartes. Les neuf rôles sont ceux du contrat des vues, et le catalogue est
 * FERMÉ ici : une clef inconnue est refusée avec son nom plutôt que d'être
 * ignorée en silence.
 *
 * UNE VUE MONTÉE SANS CE QU'ELLE EXIGE RESTE LÉGITIME : un calendrier sans
 * `dateStart` est proposé et GRISÉ avec sa raison, jamais retiré du sélecteur.
 * Ce fichier ne l'interdit donc pas, et le garde-fou de CI le DIT en jugement.
 */
export const appScreenBindingSchema = z
  .object({
    title: appScreenSourceSchema.optional(),
    subtitle: appScreenSourceSchema.optional(),
    avatar: appScreenSourceSchema.optional(),
    status: appScreenSourceSchema.optional(),
    badges: appScreenSourceSchema.optional(),
    groupKey: appScreenSourceSchema.optional(),
    lane: appScreenSourceSchema.optional(),
    dateStart: appScreenSourceSchema.optional(),
    dateEnd: appScreenSourceSchema.optional(),
    /**
     * LES RÔLES PROPRES D'UNE VUE 100% CUSTOM (`avancement`, `lat`, `lng`).
     *
     * Ils ne sont pas dans le catalogue fermé au-dessus, et c'est exactement
     * pourquoi ils ont leur sac : les neuf rôles du contrat sont partagés par
     * toutes les vues, ceux-ci n'ont de sens que pour UNE vue et arrivent dans
     * `row.extra`. Le socle ne sait pas ce qu'ils veulent dire, il les
     * transporte ; la vue custom, elle, les attend nommément et les déclare
     * dans son `rolesEnPlus`. La CI recoupe les deux.
     */
    extra: z
      .record(
        z.string().regex(/^[a-z][a-zA-Z0-9_]*$/, "un rôle propre est un identifiant en minuscules"),
        appScreenSourceSchema,
      )
      .optional(),
    /** Les colonnes reprises du catalogue de l'écran, DANS L'ORDRE de cette vue. */
    colonnes: z.array(nomDeChampSchema).min(1).optional(),
    /** Le plan de la fiche : ses cartes, dans leur ordre. */
    cartes: z.array(appScreenCardSchema).min(1).optional(),
    /**
     * LES TOTAUX LIÉS PAR LIGNE (lot du 14/10/2026) : sur CHAQUE fiche de la
     * vue, le total de ses fiches liées (« Temps saisi » d'une tâche = somme des
     * durées de ses saisies). Même forme que le champ de total d'une carte
     * (`{ libelle, liee, total, champ }`). Le tableau en fait des colonnes après
     * les siennes, la liste et le kanban les écrivent sur la carte, à droite du
     * sous-titre. Le calcul porte sur le jeu lié CHARGÉ EN ENTIER par l'écran
     * (comme les compteurs du menu), jamais sur une page : `docs/VUES-CONTRAT.md`
     * 16.9 bis. La fiche n'en a pas besoin : elle a ses champs de total.
     */
    totauxLies: z.array(appScreenRollupFieldSchema).min(1).max(3).optional(),
    /**
     * LA SURBRILLANCE DES CARTES (kanban seulement) : des règles lues dans
     * l'ordre. La PREMIÈRE vraie donne la couleur de la bordure et de l'ombre ;
     * chaque règle vraie qui porte une étiquette pose sa pastille. Absente : la
     * seule règle « Nouveau ». Voir `appScreenHighlightSchema`.
     */
    surbrillances: z.array(appScreenHighlightSchema).min(1).max(8).optional(),
    /** Le mot du titre pour cette vue ("Matériel" plutôt que "Nom"). */
    motDuTitre: z.string().min(1).optional(),
    /** Le mot du statut pour cette vue ("État" plutôt que "Statut"). */
    motDuStatut: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((binding, ctx) => {
    for (const [nom, source] of Object.entries({ ...binding, ...binding.extra })) {
      if (source && typeof source === 'object' && 'role' in source && source.role !== undefined
        && (nom !== 'avatar' || binding.title === undefined || binding.title.role !== undefined))
        ctx.addIssue({ code: 'custom', message: 'seule la vignette derive un titre existant', path: [nom] });
    }
    /* DEUX CARTES SOUS LE MÊME ID NE SE FUSIONNENT PAS : la seconde écraserait
       la première, en silence. L'id est facultatif, l'unicité ne l'est pas. */
    const vus = new Set<string>();
    for (const carte of binding.cartes ?? []) {
      if (carte.id === undefined) continue;
      if (vus.has(carte.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `la carte "${carte.id}" est déclarée deux fois`,
        });
      }
      vus.add(carte.id);
    }
  });

/** Le détail d'une vue montée. */
export type AppScreenBinding = z.infer<typeof appScreenBindingSchema>;

/* -------------------------------------------------------------------------- */
/* Les vues 100% custom                                                       */
/* -------------------------------------------------------------------------- */

/**
 * LA CARTE D'IDENTITE D'UNE VUE 100% CUSTOM (View Canvas, règle G23).
 *
 * CE QU'ELLE EST, ET CE QU'ELLE N'EST PAS. Un descripteur de vue se coupe en
 * deux : sa carte d'identité (son libellé, son niveau, ce qu'elle exige, ses
 * rôles propres, ce qu'elle demande au chargeur, qui possède son défilement)
 * qui est de la DONNÉE, et son RENDU, qui est du React. Ce bloc porte la carte,
 * jamais le rendu : le composant et l'icône restent du TypeScript, dans le
 * dossier `views/` de l'app, et c'est là que `pnpm canvas:check` va les lire.
 *
 * LE DESCRIPTEUR GARDE SON `id` LITTÉRAL et étale cette carte par-dessus. C'est
 * ce qui permet à `scripts/check-canvas.mjs` de continuer à lire
 * `const X: ViewType = { id: '<app>.<vue>' ... }` sans qu'une ligne du
 * garde-fou change : ce qu'il voit reste ce que l'app écrit.
 *
 * LA DÉROGATION NE SE PREND PAS ICI. Elle est accordée par le fondateur et
 * déclarée dans `canvas.views` de `taki.app.json` ; la CI recoupe les deux dans
 * les deux sens, pour qu'une vue réglée sans accord et un accord sans vue
 * soient tous deux des fautes.
 */
export const appScreenCustomViewSchema = z
  .object({
    /** L'id namespacé de la vue, tel que le descripteur TypeScript l'écrit. */
    id: vueIdSchema,
    /** Le libellé du sélecteur : c'est lui qu'on lit, jamais l'id. */
    label: z.string().min(1),
    /** `collection` (une liste) ou `record` (le détail d'un élément). */
    niveau: z.enum(["collection", "record"]).optional(),
    /** Ce que la vue EXIGE pour être proposée. Non satisfait : grisée avec sa raison. */
    exige: z.array(z.string().min(1)).optional(),
    /** Ses rôles PROPRES, qui arrivent dans `row.extra` plutôt que dans le contrat. */
    rolesEnPlus: z.array(z.string().min(1)).optional(),
    /** Ce qu'elle demande au chargeur : une page, une fenêtre, ou tout le jeu. */
    demande: z.enum(["page", "window", "all"]).optional(),
    /** Qui possède la barre de défilement : la zone du gabarit, ou la vue. */
    defilement: z.enum(["zone", "self"]).optional(),
    /**
     * LE JEU QU'UNE VUE DE FICHE MONTRE (chantier `fiche-suivi`) : une vue de
     * niveau `record` se déclare PAR JEU, elle s'offre dans toute porte qui le
     * montre. Absent sur une vue de fiche : toutes les portes de l'écran.
     * Interdit sur une vue de collection, qui suit le catalogue de sa porte.
     */
    jeu: z
      .object({ family: z.string().min(1), type: z.string().min(1).optional() })
      .strict()
      .optional(),
  })
  .strict()
  .refine((vue) => vue.jeu === undefined || vue.niveau === "record", {
    message: "seule une vue de fiche (niveau record) déclare son jeu",
  });

/** Une vue 100% custom déclarée. */
export type AppScreenCustomView = z.infer<typeof appScreenCustomViewSchema>;

/* -------------------------------------------------------------------------- */
/* Le tri, les filtres, les favoris, les actions et les stats                 */
/* -------------------------------------------------------------------------- */

/**
 * UNE CLEF DE TRI : un CHAMP, ou l'un des cinq RÔLES que le socle sait trier
 * sans colonne (`sortValueOf` les lit sur la ligne projetée). Exactement une
 * des deux, jamais les deux.
 *
 * Pourquoi les rôles sont là : la pile de tri est UNE pour tout l'écran, et
 * « trier par nom » veut dire trier sur ce que la ligne AFFICHE en titre, pas
 * sur un champ que telle vue reprend en colonne et telle autre non.
 */
const clefDeTriSchema = z
  .object({
    champ: nomDeChampSchema.optional(),
    role: z.enum(["title", "subtitle", "status", "dateStart", "dateEnd"]).optional(),
  })
  .strict();

/** Les deux sens d'un niveau de tri. Le socle compare, le fichier nomme. */
const sensSchema = z.enum(["asc", "desc"]);

/**
 * UNE OPTION DU CATALOGUE DE TRI (le popover de l'outil tri).
 *
 * `label` suit la MÊME règle que celui d'une colonne : OBLIGATOIRE sur un rôle
 * (rien d'autre ne connaît le mot de « Nom » ou d'« État »), INTERDIT sur un
 * champ (son libellé vit déjà dans `taki.fields.json`, ou dans le catalogue de
 * colonnes quand le champ est natif). L'écrire ici en ferait le même fait à
 * deux endroits.
 */
export const appScreenSortOptionSchema = clefDeTriSchema
  .extend({ label: z.string().min(1).optional() })
  .strict();

/** Un niveau de la pile de tri : une clef, un sens. De la donnée pure. */
export const appScreenSortLevelSchema = clefDeTriSchema
  .extend({ sens: sensSchema })
  .strict();

/**
 * LE CATALOGUE DE TRI, ET LA PILE D'ARRIVÉE.
 *
 * IL EST DÉCLARÉ PAR L'ÉCRAN, PAS PAR LA PORTE, et c'est le socle qui le veut
 * ainsi : `sortOptions` est une prop du gabarit, passée une fois pour toutes
 * les portes de l'écran. Le studio le montre sous chaque porte EN DISANT qu'il
 * est partagé, plutôt que de faire croire à un réglage par porte que le code
 * ne tient pas.
 *
 * SANS CATALOGUE, L'OUTIL TRI N'EST PAS RENDU DU TOUT : ce n'est donc pas une
 * décoration, c'est ce qui allume le geste.
 */
export const appScreenSortSchema = z
  .object({
    options: z.array(appScreenSortOptionSchema).min(1),
    /** La pile ORDONNÉE d'arrivée : le niveau 1 commande, les suivants départagent. */
    defaut: z.array(appScreenSortLevelSchema).min(1).optional(),
  })
  .strict();

/** Le catalogue de tri d'un écran. */
export type AppScreenSort = z.infer<typeof appScreenSortSchema>;

/**
 * LE CATALOGUE DE FILTRES D'UNE PORTE.
 *
 * `parChamp` est LE mécanisme du socle : une valeur du catalogue d'un champ à
 * choix devient un filtre, et la comparaison (une ÉGALITÉ) appartient au socle.
 * Le fichier nomme le champ, il n'écrit ni opérateur ni condition : c'est
 * exactement la frontière que la ligne rouge trace.
 *
 * Tout filtre qui n'est pas « ce champ vaut cette valeur » se NOMME (`fn`), et
 * son prédicat reste une fonction TypeScript de l'app. Il n'y aura jamais de
 * `cond: "superieur"` ici : le jour où il en faudrait un, c'est un prédicat
 * nommé de plus, pas une clef de plus.
 */
export const appScreenFiltersSchema = z
  .object({
    /** Un filtre par valeur du catalogue de ce champ à choix. */
    parChamp: nomDeChampSchema.optional(),
    /** Les filtres qui ne sont pas une valeur de catalogue : chacun nomme son prédicat. */
    autres: z
      .array(
        z
          .object({
            id: z.string().regex(/^[a-z][a-z0-9_-]*$/, "un id de filtre est en minuscules"),
            label: z.string().min(1),
            fn: nomDeFonctionSchema.optional(),
            condition: conditionSchema.optional(),
          })
          .strict().refine(f => Number(f.fn !== undefined) + Number(f.condition !== undefined) === 1, "un filtre porte fn ou condition"),
      )
      .min(1)
      .optional(),
  })
  .strict();

/** Le catalogue de filtres d'une porte. */
export type AppScreenFilters = z.infer<typeof appScreenFiltersSchema>;

/**
 * UN FAVORI PRESET : un état {filtres, pile de tri} NOMMÉ, jamais une donnée
 * métier. C'était déjà de la donnée pure dans le code des apps ; il change de
 * fichier, pas de nature.
 *
 * Les presets de la porte sont servis AVANT les favoris que l'utilisateur
 * garde, et ils ne s'oublient pas : en retirer un le retire pour tout le monde.
 */
export const appScreenFavoriteSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/, "un id de favori est en minuscules, tirets admis"),
    label: z.string().min(1),
    /** Les ids de filtres qu'il pose. Vide = aucun filtre, donc tout le jeu. */
    filtres: z.array(z.string().min(1)),
    /** La pile de tri qu'il pose, ORDONNÉE. */
    tri: z.array(appScreenSortLevelSchema),
  })
  .strict();

/** Un favori preset. */
export type AppScreenFavorite = z.infer<typeof appScreenFavoriteSchema>;

/**
 * LA FENÊTRE QU'UNE ACTION DEMANDE AVANT DE SE FAIRE : cinq CHAÎNES littérales,
 * et rien ne s'y interpole. Ce n'est pas un gabarit, c'est un texte.
 *
 * Les deux fenêtres du produit (archiver, supprimer) restent celles du socle :
 * elles tiennent l'accord du nombre, et une app ne les réécrit pas.
 */
export const appScreenConfirmSchema = z
  .object({
    ton: z.enum(["neutral", "danger"]),
    titre: z.string().min(1),
    corps: z.string().min(1),
    faire: z.string().min(1),
    annuler: z.string().min(1).optional(),
  })
  .strict();

/**
 * UNE ACTION DE PORTE. Tout y est de la donnée SAUF son effet, qui est une
 * fonction (ou une fenêtre) de l'app que le fichier NOMME.
 *
 * `niveau` DIT OÙ ELLE SE RENDRA. Absent ou `"collection"` : la rangée 3a, comme
 * toujours. `"fiche"` (28/10/2026) : le geste porte sur UNE fiche, il se rend
 * dans la rangée de la fiche ouverte de cette porte, à gauche après la sortie,
 * sur TOUTES les vues de la fiche (la Fiche standard comme une vue Canvas), et
 * jamais en 3a. Son effet est soit `fn` (un effet d'app, `ficheId` = la fiche
 * ouverte), soit `fenetre` (une FENÊTRE d'app, que l'app fournit par
 * `definirAppDeclaree({ fenetres })` et que l'écran monte au clic).
 * `docs/socle/actions-fiche-sans-plafond.md`.
 *
 * `kind: "add"` : le châssis rend « + Ajouter », en premier, en lime, icône
 * figée (règle G5). Le libellé déclaré ici est alors IGNORÉ à l'écran, et le
 * studio le dit sur la ligne plutôt que de laisser saisir un mot qui ne
 * s'affichera nulle part. L'ajout est un geste de COLLECTION : il n'a pas de
 * sens sur une fiche ouverte.
 *
 * `fn` EST FACULTATIF SUR UNE ACTION D'AJOUT, et sur elle seule (09/09/2026) :
 * « + Ajouter » n'ouvre plus une fenetre ecrite par l'app, il ouvre la FICHE
 * VIDE que le socle tient. Une action d'ajout sans `fn` est donc une action
 * TENUE PAR LE SOCLE, qui n'a rien a nommer. Une action d'ajout qui garde son
 * `fn` continue de marcher a l'identique : les apps non migrees ne bougent pas.
 */
export const appScreenActionSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/, "un id d'action est en minuscules, tirets admis"),
    label: z.string().min(1),
    kind: z.literal("add").optional(),
    variant: z.enum(["primary", "secondary"]).optional(),
    /** Où le geste se rend : la rangée 3a (défaut) ou la rangée de la fiche ouverte. */
    niveau: z.enum(["collection", "fiche"]).optional(),
    /**
     * L'effet, NOMMÉ. L'app le fournit au montage ; un nom introuvable est un
     * refus. Absent : seule une action `kind: "add"` s'en passe (le socle la
     * tient), et le refus ci-dessous le dit. `socle:<nom>` désigne un effet
     * tenu par le socle (`EFFETS_DU_SOCLE`), que l'app n'a pas à fournir.
     */
    fn: nomDEffetSchema.optional(),
    /**
     * LA FENÊTRE D'APP, NOMMÉE, d'un geste de niveau fiche : un composant de
     * l'app, rendu par l'écran à côté du gabarit, qui reçoit la fiche ouverte.
     * Exclusive avec `fn`. Un effet ne peut ni ouvrir de fenêtre ni utiliser un
     * hook ; la fenêtre, si, c'est un composant React ordinaire.
     */
    fenetre: nomDeFonctionSchema.optional(),
    confirmation: appScreenConfirmSchema.optional(),
  })
  .strict()
  .superRefine((action, ctx) => {
    const faute = (message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    };
    if (action.fenetre !== undefined) {
      if (action.niveau !== "fiche") {
        faute('une action qui ouvre une fenêtre est de niveau "fiche" : sans niveau, elle ne saurait pas quelle fiche donner');
      }
      if (action.fn !== undefined) faute('une action nomme son effet `fn` OU sa `fenetre`, pas les deux');
    }
    if (action.niveau === "fiche") {
      if (action.confirmation !== undefined) {
        faute('une action de niveau "fiche" ne porte pas de `confirmation` : la rangée de la fiche ne la rend pas, la fenêtre de l\'app confirme elle-même');
      }
      if (action.kind === "add") faute('un ajout est un geste de collection : "niveau: fiche" n\'a pas de sens avec kind "add"');
      if (action.fn === undefined && action.fenetre === undefined) {
        faute('une action de niveau "fiche" nomme son effet `fn` ou sa `fenetre`');
      }
      /* Les effets du socle annoncent leur avis sur le bouton de la rangée 3a,
         et « Exporter en PDF » a déjà sa place dans le menu « ... » de la fiche :
         un geste de fiche nomme un effet de l'app. */
      if (action.fn !== undefined && action.fn.startsWith(PREFIXE_EFFET_SOCLE)) {
        faute('une action de niveau "fiche" nomme un effet de l\'app, pas un effet `socle:`');
      }
      return;
    }
    /*
     * Une action sans effet et sans `kind: "add"` ne ferait RIEN au clic, sans
     * la moindre erreur nulle part : un bouton mort dans la rangee 3a. Le
     * silence est le degat, donc il se refuse ici.
     */
    if (action.kind !== "add" && action.fn === undefined) {
      faute(
        'une action sans kind "add" nomme son effet `fn` ; sans `fn`, seule une action ' +
          "d'ajout est tenue par le socle",
      );
    }
  });

/** Une action de porte. */
export type AppScreenAction = z.infer<typeof appScreenActionSchema>;

/**
 * LES NEUF CALCULS D'UNE STAT, table FERMÉE, la même que celle du formulaire de
 * brief. Ce sont des NOMS d'agrégats que le socle sait faire, jamais des
 * formules : le précédent existe déjà sur le pied de colonne d'un tableau
 * (`total: "somme"`), que `TableView` calcule depuis son nom.
 */
const calculDeStatSchema = z.enum([
  "nb",
  "total",
  "moyenne",
  "min",
  "max",
  "nbVaut",
  "nbDistinct",
  "nbRempli",
  "nbVide",
]);

/**
 * UNE STAT DE LA ZONE 2c : un champ, un calcul NOMMÉ, un libellé.
 *
 * ELLES SE CALCULENT SUR LE JEU VIVANT DE LA PORTE : archives exclues, AVANT la
 * recherche et les filtres. Une stat ne bouge donc pas quand on filtre, et le
 * plafond de 3 est tenu par le châssis (la quatrième n'est pas rendue).
 *
 * `nbVaut` EST LA SEULE CLEF QUI FAIT ENTRER UNE VALEUR DE COMPARAISON dans le
 * fichier, et elle est bornée exprès : l'opérateur, lui, n'y entre jamais.
 * « Le nombre de fiches dont X vaut V » est un nom d'agrégat, pas une
 * condition ; le jour où il faudrait comparer autrement, c'est une stat de
 * l'app en TypeScript, pas un opérateur de plus ici.
 */
export const appScreenStatSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/, "un id de stat est en minuscules, tirets admis"),
    /** Le mot sous la valeur ("matériels", "disponibles"). */
    label: z.string().min(1),
    calcul: calculDeStatSchema,
    /** Le champ sur lequel le calcul porte. Absent seulement pour `nb`. */
    champ: nomDeChampSchema.optional(),
    /** La valeur comparée, pour `nbVaut` et pour lui seul. */
    valeur: z.string().min(1).optional(),
    /** L'habillage du nombre, NOMMÉ (des euros, une durée). Le socle ne formate rien. */
    habillage: nomDeFonctionSchema.optional(),
  })
  .strict()
  .superRefine((stat, ctx) => {
    const faute = (message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    };
    if (stat.calcul === "nb") {
      if (stat.champ !== undefined) faute('le calcul "nb" compte les fiches : il ne porte aucun champ');
    } else if (stat.champ === undefined) {
      faute(`le calcul "${stat.calcul}" porte sur un champ : il en manque un`);
    }
    if (stat.calcul === "nbVaut") {
      if (stat.valeur === undefined) faute('le calcul "nbVaut" compare à une valeur : elle manque');
    } else if (stat.valeur !== undefined) {
      faute(`le calcul "${stat.calcul}" ne compare à rien : retirer "valeur"`);
    }
  });

/** Une stat de porte. */
export type AppScreenStat = z.infer<typeof appScreenStatSchema>;

/**
 * LES TROIS GESTES DE FICHE, en trois booléens.
 *
 * Ils sont déclarés par l'ÉCRAN, comme le socle le prévoit : une porte qui ne
 * dit rien retombe sur lui. Ce sont des INTERRUPTEURS, pas des fonctions :
 * l'écriture (`sdk.<famille>.archive`) reste du TypeScript, le fichier dit
 * seulement quels gestes cet écran offre.
 *
 * DÉCOCHER NE CACHE PAS L'ENTRÉE : le menu « ... » la rend GRISÉE avec sa
 * raison, toujours (règle G16). Un geste qui apparaît et disparaît selon l'app
 * fabrique exactement le « pourquoi je ne peux pas archiver ici » qu'on évite.
 */
export const appScreenVerbsSchema = z
  .object({
    archive: z.boolean().optional(),
    unarchive: z.boolean().optional(),
    remove: z.boolean().optional(),
  })
  .strict();

/** Les trois gestes de fiche d'un écran. */
export type AppScreenVerbs = z.infer<typeof appScreenVerbsSchema>;

/* -------------------------------------------------------------------------- */
/* Les portes                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * UN LIEN ENTRE DEUX PORTES DU MÊME ÉCRAN : « les interventions de CE
 * matériel », « les heures de CE projet ».
 *
 * CE QU'IL EST : trois noms. La porte LIÉE (donc son jeu), le champ de ce jeu
 * qui porte l'id de la fiche parente, et un id pour que les cartes le citent.
 * Il ne décrit ni requête, ni jointure, ni cascade : c'est le socle qui demande
 * les fiches liées au serveur, avec le champ que ce lien NOMME.
 *
 * IL RESTE DANS L'ÉCRAN, et ce n'est pas une limite provisoire : les deux
 * portes partagent le catalogue de vues, le catalogue de colonnes et le tri de
 * l'écran. Lier une porte d'un AUTRE écran demanderait de rapatrier tout cela,
 * c'est-à-dire de déclarer un second écran dans le premier.
 *
 * `par` EST UN NOM NU, jamais préfixé : c'est le champ du jeu LIÉ, tel que son
 * `taki.fields.json` le nomme (ou un champ natif de sa famille). Le préfixe
 * `socle:` dit dans quel espace lire un champ de LA fiche courante ; ici, c'est
 * le jeu lié qui répond, et c'est la CI qui recoupe le nom avec lui.
 */
export const appScreenLinkSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/, "un id de lien est en minuscules, tirets admis"),
    /** L'id d'une AUTRE porte du même écran, qui déclare son `jeu`. */
    porte: z.string().min(1),
    /** Le champ du jeu lié qui porte l'id de la fiche parente (`assetId`). */
    par: z
      .string()
      .regex(/^[a-z][a-zA-Z0-9_]*$/, "le champ d'un lien est un nom nu, jamais socle:<champ>"),
  })
  .strict();

/** Un lien déclaré par une porte. */
export type AppScreenLink = z.infer<typeof appScreenLinkSchema>;

/**
 * UN ÉTAT DE PORTE QUI PARLE : le titre et, sous lui, l'indication qui dit quoi
 * faire (« Ajoutez votre premier contact »). Deux phrases de l'app, jamais un
 * rendu : le socle les pose dans l'état vide ou introuvable de la zone 5.
 */
export const appScreenEtatSchema = z
  .object({
    titre: z.string().min(1),
    indication: z.string().min(1).optional(),
  })
  .strict();

/** Un état de porte déclaré (vide, introuvable). */
export type AppScreenEtat = z.infer<typeof appScreenEtatSchema>;

/**
 * UNE PORTE : une entrée du menu, donc un JEU DE DONNÉES de l'app.
 *
 * `id` est IMMUABLE : c'est la valeur du paramètre `s` de l'adresse et la clef
 * des préférences de favoris. `label`, lui, se renomme, et c'est toute la
 * différence : renommer une porte est une écriture de chaîne dans ce fichier.
 *
 * CE QUE LA PORTE PORTE, ET CE QU'ELLE NE PORTERA JAMAIS. Ses filtres, ses
 * favoris, ses actions et ses stats sont ici, parce que ce sont des noms et des
 * mots. Le CHARGEMENT de son jeu, l'EFFET de ses actions et le PRÉDICAT d'un
 * filtre hors catalogue restent du TypeScript : ce fichier les NOMME.
 *
 * SON CATALOGUE DE TRI EST AILLEURS, sur l'écran : le socle le passe une fois
 * pour toutes les portes (`sortOptions` est une prop du gabarit). Le mettre ici
 * promettrait un réglage par porte que le code ne tient pas.
 */
export const appScreenDoorSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/, "un id de porte est en minuscules, tirets admis"),
    label: z.string().min(1),
    /** Sous-catalogue de cette porte. Absent : héritage du catalogue de l'écran. */
    vues: z.array(vueIdSchema).min(1).optional(),
    arrivee: vueIdSchema.optional(),
    /** Le placeholder de la recherche. Absent : le défaut du socle. */
    placeholder: z.string().min(1).optional(),
    /**
     * CE QUE LA PORTE DIT QUAND LE JEU EST VIDE (zone 5, état vide) et QUAND LA
     * FICHE DEMANDÉE N'EXISTE PAS (état introuvable). Deux textes de l'app, dans
     * son vocabulaire (« Aucun contact », « Ce contact n'existe pas ou a été
     * supprimé »), que l'écran générique rend à la place des phrases neutres du
     * socle. Absents : le socle parle, en termes génériques.
     *
     * Ils vivaient dans le code de chaque écran (`VIDES`, `INTROUVABLES`) ; une
     * app sans code n'a plus d'endroit où les poser, donc ils se déclarent.
     */
    vide: appScreenEtatSchema.optional(),
    introuvable: appScreenEtatSchema.optional(),
    /** Les modèles de fiches sont activés sur cette porte (« Enregistrer comme modèle », la flèche de « Ajouter »). Absent : non. */
    modeles: z.boolean().optional(),
    /**
     * Les plans récurrents sont activés sur cette porte (« Créer un plan
     * récurrent », sur une fiche ou sur un modèle). Absent : non. Même contrat
     * que `modeles`, et pour la même raison : le jeu dit où naîtraient les
     * fiches, il ne dit pas que la porte en veut. Exige `modeles` : le contenu
     * d'un plan est un modèle.
     */
    recurrences: z.boolean().optional(),
    /**
     * Cette porte se TRANCHE (filtres, favoris, étoile). Absent : oui.
     * `false` ne retire ni l'outil filtre ni l'entrée Archives : le socle les
     * garde, seule la découpe du jeu disparaît.
     */
    tranchable: z.boolean().optional(),
    /**
     * Cette porte s'affiche dans la NAVIGATION (le menu de l'app). Absent : oui.
     * `false` : on n'y entre que par un lien d'une autre porte (le clic qui
     * entre dans une fiche, une carte liée). Le détail : `docs/socle/porte-fiche.md`.
     */
    navigation: z.boolean().optional(),
    /**
     * Le TABLEAU de cette porte (`core.tableau`) s'ÉDITE EN PLACE : une cellule
     * se modifie sans ouvrir la fiche. Absent : non, le tableau se lit et un
     * clic ouvre la fiche. Ne s'écrit que `true` (désactiver retire la clef) et
     * n'a de sens que si la porte monte `core.tableau`.
     */
    modifierDansLeTableau: z.boolean().optional(),
    /**
     * LE JEU que cette porte montre. `family` et `type` DÉSIGNENT le jeu déclaré
     * dans `taki.fields.json` : ils le pointent, ils ne le redécrivent pas.
     * `nom` est le nom PARLANT du set, saisi, celui qu'on lit à l'écran à la
     * place de la famille du socle. `singulier`, facultatif, nomme UNE fiche
     * de ce jeu (« Tâche de projet », « Saisie de temps ») là où l'on choisit
     * ce que l'on crée (Flux) ; absent, `nom` le remplace.
     */
    jeu: z
      .object({
        nom: z.string().min(1),
        singulier: z.string().min(1).max(40).optional(),
        family: z.string().min(1),
        type: z.string().min(1).optional(),
      })
      .strict()
      .optional(),
    /**
     * CETTE PORTE EST UN SPACE CANVAS (règle G23). La dérogation ne se PREND
     * pas ici : elle est accordée par le fondateur et déclarée dans
     * `taki.app.json`. Ce bloc la met en oeuvre, en disant ce que la porte
     * garde de la rangée 3.
     */
    canvas: z
      .object({ keeps: z.array(z.enum(["3a", "3b"])).optional(), elements: z.array(z.enum(["rail", "identite", "portes", "recherche", "statistiques", "actions", "filtres", "tri", "menu", "vues", "tiroir", "fond"])).optional(), alimente: z.array(z.enum(["rail", "recherche", "statistiques", "actions", "filtres", "tri", "menu", "vues", "tiroir"])).optional() })
      .strict()
      .refine((cadre) => cadre.elements === undefined || cadre.keeps === undefined, "elements et keeps ne peuvent pas coexister")
      .optional(),
    /**
     * LE CATALOGUE DE FILTRES (popover de l'outil filtre, zone 3b). Multi-choix
     * ADDITIF : une fiche passe si AU MOINS UN filtre posé la retient. Aucun
     * filtre posé = tout le jeu.
     */
    filtres: appScreenFiltersSchema.optional(),
    /** Les favoris presets de la porte (zone 4c), servis avant ceux de l'utilisateur. */
    favoris: z.array(appScreenFavoriteSchema).min(1).optional(),
    /**
     * LA TRANCHE : la porte ne montre QUE les fiches de son jeu qui passent
     * cette condition (« Mes tâches » : `assigne` est moi). Ce n'est pas un
     * filtre : ni puce, ni croix, rien a retirer. Compteur, vues, recherche,
     * stats et archives suivent ; un champ que la tranche veut « moi » est
     * prerempli a la creation. Deux portes peuvent viser le meme jeu, l'une
     * entiere, l'autre tranchee. Absente : tout le jeu.
     */
    tranche: conditionSchema.optional(),
    /** Les actions de la rangée 3a. */
    actions: z.array(appScreenActionSchema).min(1).optional(),
    /**
     * CE QUE LA FICHE VIDE PORTE DEJA a l'ouverture de « + Ajouter ».
     *
     * POURQUOI PAR PORTE ET PAS PAR CHAMP. « Le materiel nait disponible » est
     * une regle de la PORTE, pas du champ `status` : le meme champ, sur la porte
     * des interventions, naitrait autrement. L'ecrire sur le champ obligerait a
     * le redire, ou a le contredire, des la deuxieme porte.
     *
     * LES CLEFS SONT DES NOMS DE CHAMP, le vocabulaire des colonnes et des
     * cartes : `socle:status` pour un champ NATIF de la famille, `category` pour
     * un champ declare du jeu de la porte. Le socle les traduit en CHEMINS DE
     * STOCKAGE (racine ou `customFields`) au moment d'ouvrir le brouillon : les
     * ecrire ici en chemin ferait ranger une valeur au mauvais etage, ce qui
     * cree la fiche sans erreur et la rend invisible dans toutes les cartes.
     *
     * TROIS TYPES DE VALEUR : une chaine, un nombre, un booleen, qui se lisent
     * tels quels. Un CALCUL (le jour, l'instant, qui ouvre la fiche) n'est pas
     * de la donnee : il se nomme par un jeton.
     *
     * LES JETONS DU SOCLE (`JETONS_DU_SOCLE`) : une chaine de la liste fermee,
     * que le socle RESOUT a l'ouverture du brouillon
     * (`"socle:workerId": "socle:personneConnectee"` pose la personne qui ouvre
     * la fiche, `"date": "socle:aujourdhui"` le jour d'alors dans le fuseau de
     * l'org, `"debut": "socle:maintenant"` l'instant d'alors). Le calcul reste
     * au socle, le fichier ne fait que le nommer ; un jeton sans reponse laisse
     * la case vide.
     */
    creation: z
      .object({
        defauts: z.record(valeurDeDefautSchema).optional(),
      })
      .strict()
      .optional(),
    /** Les stats de la zone 2c. Le châssis en rend TROIS au plus. */
    stats: z.array(appScreenStatSchema).min(1).optional(),
    /**
     * LES LIENS DE CETTE PORTE vers d'autres portes du même écran. Ils ne
     * s'affichent nulle part par eux-mêmes : ce sont les CARTES de la fiche
     * (`liee`) et les CHAMPS DE TOTAL qui les citent, par leur id.
     */
    liens: z.array(appScreenLinkSchema).min(1).optional(),
    /**
     * AU CLIC SUR UNE FICHE de cette porte, depuis une vue de collection.
     * Absent : la fiche s'ouvre, comme partout. Présent : on entre dans la
     * porte LIÉE par `lien` (un lien de CETTE porte), filtrée sur la fiche
     * cliquée, dans la vue `vue` (absente : l'arrivée de la porte liée).
     * Le détail : `docs/socle/porte-fiche.md`.
     */
    auClic: z
      .union([
        z.object({ lien: z.string().min(1), vue: vueIdSchema.optional() }).strict(),
        /* LA FICHE S'OUVRE DANS CETTE VUE DE FICHE (chantier `fiche-suivi`) :
           une vue de niveau `record` du jeu de la porte. Le choix de chacun
           (« Ouvrir les fiches sur ») l'emporte. */
        z.object({ vueDeFiche: vueIdSchema }).strict(),
        /* LA FICHE S'OUVRE DANS UNE AUTRE PORTE, de cette app (`app` absent)
           ou d'une autre app installée. Une porte Canvas la reçoit dans ses
           props (`ficheOuverte`). */
        z
          .object({
            porte: z.string().regex(/^[a-z][a-z0-9-]*$/, "un id de porte est en minuscules, tirets admis"),
            app: z.string().min(1).optional(),
          })
          .strict(),
      ])
      .optional(),
    /**
     * LES EN-TÊTES DE FICHE, les champs à valeurs de la rangée 3 (chantier
     * `fiche-suivi`, `docs/socle/fiche-suivi.md` section 2) : une LISTE
     * ORDONNÉE, chacun un champ liste du jeu de la porte (`socle:status` pour
     * un natif à catalogue, la clef d'un champ déclaré), montré en ÉTAPES
     * (`flux`, un parcours ordonné) ou en ÉTIQUETTES (`selection`, des valeurs
     * sans ordre), un champ au plus une fois. Un clic écrit la valeur. Absent :
     * rien dans la rangée, comme avant ; une liste vide ne se pose pas.
     *
     * L'ANCIENNE FORME, un objet `{ champ, mode }` seul, reste ACCEPTÉE et vaut
     * une liste d'un élément : rien de déjà écrit ne casse. Tout ce qui écrit
     * pose la liste ; tout ce qui lit passe par `enTetesDeLaPorte`
     * (`jeux-partages.mjs`), jamais par la clef directement.
     */
    etapes: z.union([z.array(enTeteDeFicheSchema).min(1, "aucun en-tête de fiche : retire la clef"), enTeteDeFicheSchema]).optional(),
    /**
     * LES VUES DE FICHE QUE CETTE PORTE MONTRE, parmi celles de son jeu (les
     * vues Canvas de niveau fiche et les vues HTML). `core.fiche` est
     * implicite, toujours la première, et ne s'écrit pas. Absent : les vues
     * HTML du jeu seulement, une vue Canvas de fiche reste ÉTEINTE tant
     * qu'une porte ne la cite pas (`vuesDeFicheDeLaPorte`, décision du
     * 27/10) ; vide : la Fiche seule.
     */
    vuesFiche: z.array(vueIdSchema).optional(),
    /**
     * LE DÉTAIL PAR VUE DE CETTE PORTE, quand il n'est pas celui de l'écran.
     *
     * POURQUOI IL EXISTE, ET CE QU'IL A COÛTÉ. Le détail par vue vivait
     * UNIQUEMENT sur l'écran (`AppScreen.vb`), donc un écran dont deux portes
     * montrent deux JEUX différents racontait les deux avec les champs d'un
     * seul : la porte « Tickets » affichait les colonnes de « Voitures », et un
     * rôle qui cite un champ de l'autre jeu fait JETER l'assemblage au montage,
     * avec le nom du champ. Constaté sur deux apps réelles.
     *
     * LA RÈGLE DE RÉSOLUTION, UNE SEULE ET ÉCRITE ICI : le `vb` de la PORTE
     * s'il est déclaré, SINON celui de l'écran. Jamais une fusion des deux :
     * mélanger le titre d'un jeu aux colonnes d'un autre produirait une vue que
     * personne n'a déclarée. Une porte qui ne dit rien garde donc exactement le
     * comportement d'avant, et aucun fichier existant ne change de sens.
     *
     * `detailParVue(ecran, porteId)` porte cette règle en code, et c'est la
     * seule implémentation : le socle ne la refait nulle part ailleurs.
     */
    vb: z.record(vueIdSchema, appScreenBindingSchema).optional(),
  })
  .strict()
  .superRefine((porte, ctx) => {
    const faute = (message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    };
    const unique = (quoi: string, ids: string[]): void => {
      const vus = new Set<string>();
      for (const id of ids) {
        if (vus.has(id)) faute(`${quoi} "${id}" est déclaré deux fois`);
        vus.add(id);
      }
    };
    /*
     * LE TROU NOMMÉ SE POINTE, IL NE SE SPÉCIALISE PAS. `porte.jeu` DÉSIGNE un
     * jeu de `taki.fields.json`, donc il en reprend la valeur telle quelle,
     * trou compris. Un `type` par-dessus un trou décrirait la spécialisation
     * d'une famille qui n'existe pas encore, et les deux fichiers ne se
     * pointeraient plus (le fichier de champs, lui, refuse ce `type`).
     */
    if (porte.jeu !== undefined && estFamilleADecider(porte.jeu.family) && porte.jeu.type !== undefined) {
      faute(`la porte "${porte.id}" n'a pas de famille : un type local n'y veut rien dire`);
    }

    unique("le favori", (porte.favoris ?? []).map((f) => f.id));
    unique("l'action", (porte.actions ?? []).map((a) => a.id));
    unique("la stat", (porte.stats ?? []).map((s) => s.id));
    unique("le filtre", (porte.filtres?.autres ?? []).map((f) => f.id));
    unique("le lien", (porte.liens ?? []).map((l) => l.id));

    /*
     * UNE PORTE NE SE LIE PAS À ELLE-MÊME. La carte liée montrerait les fiches
     * du jeu qu'elle affiche déjà, filtrées par le champ qui pointe la fiche
     * ouverte : une fiche dans sa propre fiche. Que la porte cible EXISTE et
     * qu'elle déclare un `jeu` se vérifie un cran plus haut, sur l'écran : ici,
     * on ne voit qu'une porte.
     */
    const clic = porte.auClic;
    if (clic !== undefined && "lien" in clic && !(porte.liens ?? []).some((lien) => lien.id === clic.lien)) {
      faute(`le clic de la porte "${porte.id}" ouvre le lien "${clic.lien}", qu'elle ne déclare pas`);
    }
    if (clic !== undefined && "porte" in clic && clic.app === undefined && clic.porte === porte.id) {
      faute(`le clic de la porte "${porte.id}" ouvre ses fiches dans elle-même : retire la clef`);
    }
    if (clic !== undefined && "vueDeFiche" in clic && clic.vueDeFiche === "core.fiche") {
      faute(`le clic de la porte "${porte.id}" ouvre la vue "core.fiche", le défaut : retire la clef`);
    }
    if ((porte.vuesFiche ?? []).includes("core.fiche")) {
      faute(`la porte "${porte.id}" cite "core.fiche" dans vuesFiche : la fiche y est toujours, implicite`);
    }
    unique("la vue de fiche", [...(porte.vuesFiche ?? [])]);
    /* UN CHAMP AU PLUS UNE FOIS EN TÊTE : deux pistes du même champ écriraient
       la même valeur à deux endroits. La liste BRUTE, pas `enTetesDeLaPorte`,
       qui écarte le doublon à la lecture. */
    const enTetes = porte.etapes === undefined ? [] : Array.isArray(porte.etapes) ? porte.etapes : [porte.etapes];
    unique("le champ en tête de fiche", enTetes.map((enTete) => enTete.champ));
    const [premierEnTete] = enTetes;
    if (premierEnTete !== undefined && porte.jeu === undefined) {
      faute(`la porte "${porte.id}" montre "${premierEnTete.champ}" en tête de fiche : déclare le jeu qu'elle montre`);
    }
    for (const lien of porte.liens ?? []) {
      if (lien.porte === porte.id) {
        faute(`le lien "${lien.id}" pointe sa propre porte : une fiche ne se lie pas à elle-même`);
      }
    }

    /*
     * UNE PORTE QUI NE SE TRANCHE PAS N'A NI FILTRES NI FAVORIS, et ce n'est pas
     * une préférence : `sections: false` VIDE les deux dans le socle. Les
     * déclarer ici les rendrait invisibles sans que rien ne le dise.
     */
    if (porte.tranchable === false) {
      if (porte.filtres !== undefined) faute("cette porte ne se tranche pas : ses filtres ne seraient jamais rendus");
      if (porte.favoris !== undefined) faute("cette porte ne se tranche pas : ses favoris ne seraient jamais rendus");
    }

    /*
     * LES MODÈLES DE FICHES ONT BESOIN DU JEU DE LA PORTE. « Enregistrer comme
     * modèle » doit dire DANS QUELLE famille ranger le modèle, et « Depuis un
     * modèle » ne proposer que ceux de CE sous-type : sans `jeu`, le socle grise
     * les deux gestes avec « cette app ne déclare pas le jeu de la porte », donc
     * la clef déclarerait un geste que rien ne peut rendre.
     */
    if (porte.modeles === true && porte.jeu === undefined) {
      faute("cette porte active les modèles de fiches : déclare le jeu qu'elle montre, ou retire la clef");
    }
    /* MÊME RAISON POUR LES PLANS RÉCURRENTS : le plan range la famille et le
       sous-type des fiches qu'il fera naître, et les deux se lisent sur le jeu. */
    if (porte.recurrences === true && porte.jeu === undefined) {
      faute("cette porte active les plans récurrents : déclare le jeu qu'elle montre, ou retire la clef");
    }
    /* UN PLAN RÉCURRENT S'APPUIE SUR UN MODÈLE : il ne porte que le rythme, le
       contenu des fiches qu'il fait naître vit dans le modèle qu'il cite
       (`templateId`). Sans `modeles` sur la porte, ce contenu ne se relirait ni
       ne se corrigerait nulle part : le plan créerait des fiches qu'on ne sait
       plus changer. */
    if (porte.recurrences === true && porte.modeles !== true) {
      faute("les plans récurrents s'éditent par le mode Modèles : la porte doit aussi allumer `modeles`");
    }

    /*
     * UN FAVORI POSE DES FILTRES QUI EXISTENT. Ceux d'un catalogue `parChamp`
     * ne sont connus qu'en lisant `taki.fields.json` : c'est la CI qui les
     * recoupe. Ici on refuse ce qui est vérifiable sans sortir du fichier : un
     * favori qui pose un filtre alors que la porte n'en déclare AUCUN.
     */
    if (porte.filtres === undefined) {
      for (const favori of porte.favoris ?? []) {
        if (favori.filtres.length > 0) {
          faute(`le favori "${favori.id}" pose des filtres, la porte n'en déclare aucun`);
        }
      }
    }

    /*
     * UNE SEULE ACTION D'AJOUT. Le châssis rend la première en lime et en
     * premier ; une seconde serait un second lime, que la règle G14 interdit.
     */
    const ajouts = (porte.actions ?? []).filter((a) => a.kind === "add");
    if (ajouts.length > 1) faute("deux actions d'ajout : le châssis n'en rend qu'une en lime");
  });

/** Une porte déclarée. */
export type AppScreenDoor = z.infer<typeof appScreenDoorSchema>;

/* -------------------------------------------------------------------------- */
/* L'écran                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * UN ÉCRAN : son catalogue de vues, son détail par vue, ses portes.
 *
 * `id` est le `screenId` passé au socle : il sert de clef aux préférences, il
 * ne s'affiche pas.
 */
export const appScreenSchema = z
  .object({
    id: z.string().min(1),
    /** Les vues montées, DANS L'ORDRE du sélecteur. La fiche en fait partie. */
    vues: z.array(vueIdSchema).min(1),
    /**
     * Les vues 100% CUSTOM que cet écran monte. Leur id figure dans `vues`
     * comme les autres : ce bloc ne les monte pas, il dit ce qu'elles SONT.
     */
    vuesCustom: z.array(appScreenCustomViewSchema).min(1).optional(),
    /**
     * LES VUES HTML ISOLÉES de cet écran, déposées par le MCP et publiées par
     * un administrateur (chantier `fiche-suivi`, `docs/socle/vues-html.md`).
     * Des vues de fiche : chacune montre une fiche de son jeu dans un cadre
     * fermé, sans réseau ni écriture. Elles ne figurent PAS dans `vues` : leur
     * code est de la donnée, aucun descripteur TypeScript ne les monte.
     * LA BORNE DE L'ÉCRAN (nombre et poids total, `vuesHtmlDeclareesSchema`)
     * est tenue ICI, pas seulement par le serveur : une déclaration qui la
     * dépasse ne se lit pas, quel que soit son chemin d'écriture.
     */
    vuesHtml: vuesHtmlDeclareesSchema.optional(),
    /** La vue d'arrivée. REFUSÉE si elle n'est pas dans `vues`. */
    arrivee: vueIdSchema,
    /** La porte d'arrivée. REFUSÉE si aucune porte ne porte cet id. */
    porteArrivee: z.string().min(1).optional(),
    /** Le catalogue de colonnes de l'écran, déclaré une fois pour toutes ses vues. */
    colonnes: z.array(appScreenColumnSchema).min(1).optional(),
    /** Le détail par vue : une entrée par vue montée qui a quelque chose à dire. */
    vb: z.record(vueIdSchema, appScreenBindingSchema),
    /**
     * LE CATALOGUE DE TRI, déclaré une fois pour toutes les portes de l'écran :
     * c'est ainsi que le socle le passe au gabarit. Absent : l'outil tri n'est
     * pas rendu du tout.
     */
    tri: appScreenSortSchema.optional(),
    /** Les trois gestes de fiche que cet écran offre à ses portes. */
    verbes: appScreenVerbsSchema.optional(),
    portes: z.array(appScreenDoorSchema).min(1),
  })
  .strict()
  .superRefine((ecran, ctx) => {
    const faute = (message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    };

    /*
     * LA VUE D'ARRIVÉE DOIT ÊTRE MONTÉE. Déjà payé une fois : un `defaultView`
     * qui ne figurait pas au catalogue rendait une zone 5 VIDE, sans erreur et
     * sans rien à lire. Le confronter ici coûte quatre lignes.
     */
    const montees = new Set(ecran.vues);
    for (const porte of ecran.portes) {
      const locales = new Set(porte.vues ?? ecran.vues);
      for (const vue of locales) {
        if (!montees.has(vue)) faute(`la porte "${porte.id}" propose la vue "${vue}" absente du catalogue de l'écran`);
      }
      if (porte.arrivee !== undefined && !locales.has(porte.arrivee)) {
        faute(`la vue d'arrivée de la porte "${porte.id}" n'est pas dans ses vues montées`);
      }
    }
    if (!montees.has(ecran.arrivee)) {
      faute(`la vue d'arrivée "${ecran.arrivee}" n'est pas dans les vues montées`);
    }
    /*
     * LE DÉTAIL PAR VUE SE DÉCLARE À DEUX ÉTAGES depuis que les portes peuvent
     * montrer des jeux différents : celui de l'écran, et celui d'une porte qui
     * le remplace. Les deux se vérifient de la MÊME façon, donc ils se lisent
     * dans une seule liste, chacun disant D'OÙ il vient.
     */
    /*
     * QUELLES PORTES SE SERVENT DE QUEL DÉTAIL, en plus : le détail d'une porte
     * n'est que le sien, celui de l'écran est celui de TOUTES les portes qui
     * n'en déclarent pas. La distinction ne comptait pour rien tant qu'une carte
     * ne citait que des colonnes ; une carte LIÉE, elle, cite un LIEN, et un
     * lien appartient à UNE porte. Le détail de l'écran doit donc valoir pour
     * chacune des portes qui l'héritent, sans quoi l'assemblage jetterait sur
     * l'une d'elles seulement.
     */
    const detailsParVue: {
      ou: string;
      vb: Record<string, AppScreenBinding>;
      porteurs: AppScreenDoor[];
    }[] = [
      { ou: "", vb: ecran.vb, porteurs: ecran.portes.filter((porte) => porte.vb === undefined) },
      ...ecran.portes
        .filter((porte) => porte.vb !== undefined)
        .map((porte) => ({
          ou: `la porte "${porte.id}" : `,
          vb: porte.vb as Record<string, AppScreenBinding>,
          porteurs: [porte],
        })),
    ];
    for (const { ou, vb } of detailsParVue) {
      for (const vue of Object.keys(vb)) {
        if (!montees.has(vue)) faute(`${ou}la vue "${vue}" a un détail mais n'est pas montée`);
      }
    }

    /*
     * UNE VUE CUSTOM DÉCRITE MAIS NON MONTÉE est une carte d'identité que
     * personne ne lit : le sélecteur ne la propose pas, le descripteur étale
     * une carte que rien ne réclame, et la dérogation qui la couvre devient
     * invisible. Le catalogue des vues du socle, lui, n'est pas connu ici : la
     * CI relit `packages/ui/src/views/core/index.ts` et refuse un id de `vues`
     * qui n'est ni du socle ni décrit ici.
     */
    const custom = new Set<string>();
    for (const vue of ecran.vuesCustom ?? []) {
      if (custom.has(vue.id)) faute(`la vue custom "${vue.id}" est décrite deux fois`);
      custom.add(vue.id);
      /* UNE VUE DE FICHE N'EST PAS MONTÉE PAR `vues` : elle s'offre par son
         jeu (`vuesDeFicheDeLaPorte`), dans toute porte qui le montre. */
      if (vue.niveau !== "record" && !montees.has(vue.id)) {
        faute(`la vue custom "${vue.id}" est décrite mais n'est pas montée`);
      }
    }

    const ids = new Set<string>();
    for (const porte of ecran.portes) {
      if (ids.has(porte.id)) faute(`la porte "${porte.id}" est déclarée deux fois`);
      ids.add(porte.id);
    }

    /*
     * LES VUES DE FICHE D'UNE PORTE (chantier `fiche-suivi`) : les vues custom
     * de niveau `record` et les vues HTML de SON jeu. `vuesFiche` choisit
     * dans cette liste (absent, les vues Canvas sont éteintes),
     * `auClic.vueDeFiche` y choisit l'arrivée : une vue d'un
     * autre jeu, ou inconnue, s'ouvrirait sur une fiche qu'elle ne sait pas
     * lire. `vuesDeFicheDeLaPorte` porte la règle, seule implémentation.
     */
    const html = new Set<string>();
    for (const vue of ecran.vuesHtml ?? []) {
      if (html.has(vue.id) || custom.has(vue.id)) faute(`la vue de fiche "${vue.id}" est déclarée deux fois`);
      html.add(vue.id);
    }
    for (const porte of ecran.portes) {
      const offertes = new Set(vuesDeFicheDeLaPorte(ecran, porte.id, { sansRestriction: true }));
      for (const vue of porte.vuesFiche ?? []) {
        if (!offertes.has(vue)) {
          faute(`la porte "${porte.id}" montre la vue de fiche "${vue}", qui n'est pas une vue de fiche de son jeu`);
        }
      }
      const clic = porte.auClic;
      if (clic !== undefined && "vueDeFiche" in clic && clic.vueDeFiche !== "core.fiche"
        && !vuesDeFicheDeLaPorte(ecran, porte.id).includes(clic.vueDeFiche)) {
        faute(`le clic de la porte "${porte.id}" ouvre la vue de fiche "${clic.vueDeFiche}", que la porte ne montre pas`);
      }
      if (clic !== undefined && "porte" in clic && clic.app === undefined) {
        const visee = ecran.portes.find((p) => p.id === clic.porte);
        if (visee === undefined) {
          faute(`le clic de la porte "${porte.id}" ouvre ses fiches dans la porte "${clic.porte}", qui n'existe pas`);
        } else if (visee !== porte && !memeJeuDePorte(porte.jeu, visee.jeu)) {
          /* UNE FICHE NE S'OUVRE QUE DANS UNE PORTE DE SON JEU (même famille,
             même type) : une porte d'un autre jeu chercherait l'id dans une
             autre collection et dirait « introuvable ». Une porte d'une AUTRE
             app ne se voit pas d'ici : le Studio la filtre à l'offre
             (`autresAppsDeclarees`). */
          faute(
            `le clic de la porte "${porte.id}" ouvre ses fiches dans la porte "${clic.porte}", ` +
              "qui ne montre pas le même jeu (famille et type)",
          );
        }
      }
      /* LE CHAMP D'UN EN-TÊTE, quand il est NATIF, se voit ici : son catalogue
         vit dans les colonnes de l'écran. Un champ déclaré se vérifie au
         montage, contre `taki.fields.json` (`fautesDesEtapes`). */
      for (const { champ, verrous = [] } of enTetesDeLaPorte(porte)) {
        if (!champ.startsWith(PREFIXE_CHAMP_SOCLE)) continue;
        const colonne = (ecran.colonnes ?? []).find((candidate) => candidate.champ === champ);
        if (colonne === undefined || (colonne.type !== "select" && colonne.options === undefined)) {
          faute(`la porte "${porte.id}" montre "${champ}" en tête de fiche : ce champ natif n'a pas de liste dans les colonnes de l'écran`);
        } else if (colonne.options !== undefined) {
          const options = colonne.options;
          for (const verrou of verrous) {
            for (const [mot, valeur] of [["avant", verrou.avant], ["à partir de", verrou.depuis]] as const) {
              if (valeur !== undefined && !options.some((option) => option.value === valeur)) {
                faute(`la porte "${porte.id}" verrouille "${champ}" ${mot} "${valeur}", qui n'est pas une valeur de ce champ`);
              }
            }
          }
        }
      }
    }

    /*
     * UN LIEN POINTE UNE PORTE DE CET ÉCRAN, ET CETTE PORTE MONTRE UN JEU. Sans
     * jeu, rien ne dit dans quelle famille demander les fiches liées : la carte
     * serait déclarée, montée, et vide sans que rien ne le dise. Même refus que
     * les modèles de fiches, pour la même raison.
     */
    for (const porte of ecran.portes) {
      for (const lien of porte.liens ?? []) {
        const cible = ecran.portes.find((p) => p.id === lien.porte);
        if (cible === undefined) {
          faute(`le lien "${porte.id}/${lien.id}" pointe la porte "${lien.porte}", qui n'existe pas`);
        } else if (cible.jeu === undefined) {
          faute(
            `le lien "${porte.id}/${lien.id}" pointe la porte "${lien.porte}", ` +
              "qui ne déclare pas le jeu qu'elle montre",
          );
        }
      }
    }

    /*
     * LA VUE D'ARRIVÉE DU CLIC est une vue de COLLECTION que la porte liée
     * monte : on y entre pour voir un jeu, jamais une fiche.
     */
    for (const porte of ecran.portes) {
      const clic = porte.auClic;
      const vue = clic !== undefined && "lien" in clic ? clic.vue : undefined;
      if (clic === undefined || !("lien" in clic) || vue === undefined) continue;
      const lien = (porte.liens ?? []).find((candidat) => candidat.id === clic.lien);
      const cible = lien === undefined ? undefined : ecran.portes.find((p) => p.id === lien.porte);
      if (cible === undefined) continue;
      if (vue === "core.fiche") {
        faute(`le clic de la porte "${porte.id}" arrive sur une fiche : la vue d'arrivée est une vue de collection`);
      } else if (!(cible.vues ?? ecran.vues).includes(vue)) {
        faute(`le clic de la porte "${porte.id}" arrive sur la vue "${vue}", que la porte "${cible.id}" ne monte pas`);
      }
    }

    /*
     * UN MÊME ID DE LIEN DÉSIGNE LA MÊME CHOSE SUR TOUT L'ÉCRAN.
     *
     * Le lien se déclare sur CHAQUE porte qui hérite de la fiche (quatre portes
     * d'Inventaire, app sortie du produit le 11/10/2026, répétaient le même lien
     * de trois lignes), et une carte de fiche
     * n'en cite que l'ID. Deux portes qui écrivent ce même id avec une autre
     * porte liée ou un autre champ font donc rendre à UNE carte deux jeux
     * différents selon la porte par laquelle on est entré : le fichier est
     * cohérent ligne à ligne, l'écran ment porte par porte, et rien ne le dit.
     * Le jour où le lien montera au niveau de l'écran, cette règle disparaîtra
     * avec la répétition qui la rend nécessaire.
     */
    const cadrageDesLiens = new Map<string, { porte: string; par: string; ou: string }>();
    for (const porte of ecran.portes) {
      for (const lien of porte.liens ?? []) {
        const vu = cadrageDesLiens.get(lien.id);
        if (vu === undefined) {
          cadrageDesLiens.set(lien.id, { porte: lien.porte, par: lien.par, ou: porte.id });
        } else if (vu.porte !== lien.porte || vu.par !== lien.par) {
          faute(
            `le lien "${lien.id}" est déclaré par les portes "${vu.ou}" et "${porte.id}" avec des ` +
              `cadrages différents ("${vu.porte}" par "${vu.par}" contre "${lien.porte}" par ` +
              `"${lien.par}") : un même id de lien désigne la même porte liée et le même champ`,
          );
        }
      }
    }

    /*
     * LE TRI D'UN FAVORI SE PREND DANS LE CATALOGUE DE L'ÉCRAN. Une clef hors
     * catalogue trierait quand même (le socle la résout sur la ligne), mais le
     * favori ne se rallumerait jamais : l'outil tri n'affiche que les clefs
     * qu'il connaît, donc la pile ne se relirait nulle part.
     */
    const clef = (niveau: { champ?: string; role?: string }): string =>
      niveau.role !== undefined ? `role:${niveau.role}` : `champ:${niveau.champ ?? ""}`;
    const triables = new Set((ecran.tri?.options ?? []).map(clef));
    const verifieTri = (ou: string, pile: readonly { champ?: string; role?: string }[]): void => {
      for (const niveau of pile) {
        if (!triables.has(clef(niveau))) {
          faute(`${ou} trie sur "${clef(niveau)}", hors du catalogue de tri de l'écran`);
        }
      }
    };
    if (ecran.tri?.defaut !== undefined) verifieTri("la pile d'arrivée", ecran.tri.defaut);
    for (const porte of ecran.portes) {
      for (const favori of porte.favoris ?? []) {
        verifieTri(`le favori "${porte.id}/${favori.id}"`, favori.tri);
      }
    }

    /*
     * UNE CLEF DE TRI PORTE UN CHAMP OU UN RÔLE, jamais les deux ni aucun, et
     * son libellé suit la règle des colonnes : obligatoire sur un rôle, interdit
     * sur un champ (il vit déjà dans le fichier de champs).
     */
    for (const [i, option] of (ecran.tri?.options ?? []).entries()) {
      const posees = [option.champ, option.role].filter((v) => v !== undefined);
      if (posees.length !== 1) {
        faute(`tri.options[${i}] : une clef de tri porte un champ OU un rôle`);
      }
      if (option.role !== undefined && option.label === undefined) {
        faute(`tri.options[${i}] : un rôle porte son libellé, rien d'autre ne le connaît`);
      }
      if (option.champ !== undefined && option.label !== undefined) {
        faute(`tri.options[${i}] : le libellé de "${option.champ}" vit dans taki.fields.json`);
      }
    }
    for (const niveau of [
      ...(ecran.tri?.defaut ?? []),
      ...ecran.portes.flatMap((porte) => (porte.favoris ?? []).flatMap((f) => f.tri)),
    ]) {
      const posees = [niveau.champ, niveau.role].filter((v) => v !== undefined);
      if (posees.length !== 1) faute("un niveau de tri porte un champ OU un rôle");
    }
    if (ecran.porteArrivee !== undefined && !ids.has(ecran.porteArrivee)) {
      faute(`la porte d'arrivée "${ecran.porteArrivee}" n'existe pas`);
    }
    /*
     * UNE PORTE HORS NAVIGATION SE REJOINT PAR UN LIEN, et ce n'est pas
     * l'arrivée : sinon personne ne pourrait l'ouvrir, ou l'écran s'ouvrirait
     * sur une porte que le menu ne montre pas.
     */
    const arriveeEffective = ecran.porteArrivee ?? ecran.portes[0]?.id;
    for (const porte of ecran.portes) {
      if (porte.navigation !== false) continue;
      if (porte.id === arriveeEffective) {
        faute(`la porte "${porte.id}" est hors navigation : elle ne peut pas être la porte d'arrivée`);
      }
      if (!ecran.portes.some((autre) => (autre.liens ?? []).some((lien) => lien.porte === porte.id))) {
        faute(`la porte "${porte.id}" est hors navigation : une autre porte doit la lier, sinon on ne l'ouvre jamais`);
      }
    }

    /*
     * LE CATALOGUE DE COLONNES. Une entrée native doit porter son libellé et son
     * type (rien d'autre ne les connaît) ; une entrée déclarée ne doit PAS les
     * porter (ils vivent dans le fichier de champs). Leurs noms qualifiés sont
     * uniques ; un natif et un champ déclaré homonymes peuvent servir deux
     * portes différentes, mais pas le même détail de porte.
     */
    const nomsComplets = new Set<string>();
    const nuDe = (nom: string): string => nom.startsWith(PREFIXE_CHAMP_SOCLE)
      ? nom.slice(PREFIXE_CHAMP_SOCLE.length) : nom;
    for (const colonne of ecran.colonnes ?? []) {
      const natif = colonne.champ.startsWith(PREFIXE_CHAMP_SOCLE);
      if (nomsComplets.has(colonne.champ)) faute(`la colonne "${colonne.champ}" est déclarée deux fois`);
      nomsComplets.add(colonne.champ);
      if (natif && (colonne.label === undefined || colonne.type === undefined)) {
        faute(`la colonne "${colonne.champ}" est native : elle doit porter label et type`);
      }
      if (!natif && (colonne.label !== undefined || colonne.type !== undefined)) {
        faute(
          `la colonne "${colonne.champ}" est déclarée dans taki.fields.json : ` +
            "son label et son type y vivent déjà",
        );
      }
      if (!natif && colonne.options !== undefined) {
        faute(
          `la colonne "${colonne.champ}" est déclarée dans taki.fields.json : ` +
            "son catalogue de valeurs y vit déjà",
        );
      }
      if (colonne.decimales !== undefined && (!natif || colonne.type !== "number")) {
        faute(
          `la colonne "${colonne.champ}" porte des décimales : elles se déclarent sur une colonne native ` +
            'de type "number" (un champ déclaré les porte dans taki.fields.json)',
        );
      }
      if (!natif && colonne.requis !== undefined) {
        faute(
          `la colonne "${colonne.champ}" est déclarée dans taki.fields.json : ` +
            "son caractère requis (`required`) y vit déjà",
        );
      }
      if (
        colonne.options !== undefined &&
        colonne.type !== "select" &&
        colonne.type !== "multiselect"
      ) {
        faute(
          `la colonne "${colonne.champ}" porte un catalogue de valeurs : ` +
            'son type doit être "select" ou "multiselect"',
        );
      }
    }
    /* Une reference qualifiee prime. L'ancien alias nu ne reste valable que
       s'il designe une seule colonne du catalogue concerne. */
    const resoudre = (nom: string, catalogue: ReadonlySet<string>): string | undefined => {
      if (catalogue.has(nom)) return nom;
      if (nom.startsWith(PREFIXE_CHAMP_SOCLE)) return undefined;
      const correspondantes = [...catalogue].filter((candidate) => nuDe(candidate) === nuDe(nom));
      return correspondantes.length === 1 ? correspondantes[0] : undefined;
    };

    /*
     * LE CATALOGUE DE COLONNES RESTE CELUI DE L'ÉCRAN, même quand une porte
     * déclare son propre détail par vue : il porte alors les colonnes des DEUX
     * jeux, et chaque porte n'en reprend que les siennes. Un catalogue par
     * porte ferait deux fois le même fait pour les colonnes partagées.
     */
    /*
     * UN LIEN CITÉ EST UN LIEN DE LA PORTE QUI PORTE LA FICHE. Une carte liée et
     * un champ de total ne connaissent qu'un id ; ce qu'il désigne (la porte
     * liée, son jeu, son champ) vit sur la porte, et c'est elle qui doit le
     * déclarer, sans quoi l'assemblage refuse au montage.
     */
    const verifieLien = (ou: string, liee: string, porteurs: readonly AppScreenDoor[]): void => {
      /*
       * UN DÉTAIL PAR VUE QUE PERSONNE N'HÉRITE. Le `vb` de l'ÉCRAN vaut pour
       * les portes qui n'en déclarent pas ; quand elles déclarent TOUTES le
       * leur, il n'a aucun porteur. La boucle ci-dessous ne tourne alors pas et
       * la carte passe, alors qu'aucune porte ne peut lui donner son lien : elle
       * est déclarée, jamais montée, et rien ne le dit.
       */
      if (porteurs.length === 0) {
        faute(
          `${ou} cite le lien "${liee}" : ce détail par vue n'est hérité par aucune porte, ` +
            'sa carte liée ne peut appartenir à aucun lien',
        );
        return;
      }
      for (const porte of porteurs) {
        if (!(porte.liens ?? []).some((lien) => lien.id === liee)) {
          faute(`${ou} cite le lien "${liee}", que la porte "${porte.id}" ne déclare pas`);
        }
      }
    };

    for (const { ou, vb, porteurs } of detailsParVue) {
      const citesParNu = new Map<string, string>();
      for (const [vue, binding] of Object.entries(vb)) {
        const reprises = new Set<string>();
        for (const nom of binding.colonnes ?? []) {
          const resolue = resoudre(nom, nomsComplets);
          if (resolue === undefined) {
            faute(`${ou}${vue} : la colonne "${nom}" n'est pas au catalogue de l'écran`);
            continue;
          }
          reprises.add(resolue);
          const nu = nuDe(resolue);
          const deja = citesParNu.get(nu);
          if (deja !== undefined && deja !== resolue) {
            faute(`${ou}les colonnes "${deja}" et "${resolue}" partagent la clé de rendu "${nu}"`);
          }
          citesParNu.set(nu, resolue);
        }
        /*
         * UNE CARTE DE CHAMPS NE MONTRE QUE DES COLONNES DE SA VUE : la fiche
         * rend ses cases par clef de colonne, un champ cité hors de cette liste
         * n'apparaîtrait nulle part et la carte mentirait sur son contenu. Une
         * carte LIÉE ne rend aucune case de cette fiche : elle n'a donc aucune
         * colonne à exiger ici.
         */
        if (
          (binding.cartes ?? []).some((carte) => estCarteDeChamps(carte)) &&
          binding.colonnes === undefined
        ) {
          faute(`${ou}${vue} : des cartes sont déclarées mais la vue n'a aucune colonne`);
        }
        /* UN TOTAL LIÉ PAR LIGNE cite un lien de la porte, comme une carte
           liée ; il ne vit que sur une vue qui a des lignes. */
        for (const entree of binding.totauxLies ?? []) {
          if (vue !== "core.tableau" && vue !== "core.liste" && vue !== "core.kanban") {
            faute(`${ou}${vue} : le total "${entree.libelle}" vit sur un tableau, une liste ou un kanban`);
          }
          verifieLien(`${ou}${vue} : le total "${entree.libelle}"`, entree.liee, porteurs);
          /* LA PONDÉRATION VIT SUR LA FICHE : le total par ligne se calcule sur le
             jeu lié que l'écran a déjà chargé en entier, et la fiche désignée
             n'y est pas. Déclarée ici, elle serait acceptée puis ignorée. */
          if (entree.fois !== undefined) {
            faute(`${ou}${vue} : le total "${entree.libelle}" est pondéré, et la pondération vit sur une carte de fiche`);
          }
        }
        /* LA SURBRILLANCE est celle d'une CARTE : seul le kanban en dessine.
           Ailleurs, elle serait déclarée et jamais rendue, sans que rien ne le
           dise. Ses champs se recoupent avec le jeu là où le jeu est connu (CI,
           `verifierChampsDEcran`), comme ceux d'un filtre. */
        if (binding.surbrillances !== undefined && vue !== "core.kanban") {
          faute(`${ou}${vue} : la surbrillance vit sur un kanban, pas sur "${vue}"`);
        }
        for (const carte of binding.cartes ?? []) {
          /*
           * UNE CARTE LIÉE NE CITE AUCUNE COLONNE : elle monte une VUE sur un
           * AUTRE jeu, avec les colonnes que la porte liée déclare. Ce qu'elle
           * doit à cet écran, c'est que sa vue soit MONTÉE : le catalogue de
           * vues est celui de l'écran, une carte n'en ouvre pas une de plus.
           */
          if (estCarteLiee(carte)) {
            verifieLien(`${ou}${vue} : la carte "${carte.titre}"`, carte.liee, porteurs);
            if (!montees.has(carte.vue)) {
              faute(
                `${ou}${vue} : la carte "${carte.titre}" monte "${carte.vue}", ` +
                  "qui n'est pas dans les vues montées",
              );
            }
            continue;
          }
          /* LA CARTE DES FICHIERS ne cite ni colonne ni lien : elle n'a rien à
             recouper ici, la capacité se lit sur le jeu. Une seule par vue. */
          if (estCarteFichiers(carte)) {
            if ((binding.cartes ?? []).filter((autre) => estCarteFichiers(autre)).length > 1) {
              faute(`${ou}${vue} : deux cartes de fichiers joints, une seule est permise`);
            }
            continue;
          }
          for (const entree of carte.champs) {
            /* UN CHAMP DE TOTAL PORTE SUR LE JEU LIÉ : son `champ` n'a rien à
               faire dans les colonnes de CETTE vue, c'est la CI qui le recoupe
               avec le jeu lié. Ce qui se vérifie ici, c'est son lien. */
            if (estChampDeTotal(entree)) {
              verifieLien(`${ou}${vue} : le total "${entree.libelle}"`, entree.liee, porteurs);
              continue;
            }
            const nom = nomDeChampDeCarte(entree);
            if (nom === undefined) continue;
            if (resoudre(nom, reprises) === undefined) {
              faute(`${ou}${vue} : la carte "${carte.titre}" cite "${nom}", hors de ses colonnes`);
            }
          }
        }
      }
    }
  });

/** Un écran déclaré. */
export type AppScreen = z.infer<typeof appScreenSchema>;

/** L'id de la vue Fiche du socle : toujours la première vue de fiche d'une porte. */
export const VUE_FICHE_DU_SOCLE = "core.fiche";

/**
 * DEUX PORTES MONTRENT-ELLES LE MÊME JEU : même famille, même type (un type
 * absent n'égale qu'un type absent). Une porte sans jeu n'a pas de fiche : elle
 * ne partage le jeu de personne. C'est la règle de « Ouvrir dans <porte> »
 * (`auClic: { porte }`, `docs/socle/porte-fiche.md`) : le schéma la juge dans
 * un écran, le Studio filtre son offre avec elle, dans l'app comme dans les
 * autres apps de l'org.
 */
export function memeJeuDePorte(
  a: { family: string; type?: string | undefined } | undefined,
  b: { family: string; type?: string | undefined } | undefined,
): boolean {
  if (a === undefined || b === undefined) return false;
  return a.family === b.family && (a.type ?? "") === (b.type ?? "");
}

/**
 * LES VUES DE FICHE QU'UNE PORTE OFFRE, `core.fiche` exclue (elle est
 * implicite, toujours la première). Les CANDIDATES sont les vues custom de
 * niveau `record` dont le jeu est celui de la porte (ou qui n'en déclarent
 * pas), puis les vues HTML de son jeu, dans l'ordre de la déclaration
 * (`sansRestriction` les rend toutes : ce que le Studio propose de cocher).
 *
 * CE QUE LA PORTE MONTRE (décision du fondateur, 27/10) :
 * - `vuesFiche` posé : les vues qu'il cite, dans SON ordre ;
 * - `vuesFiche` absent : les vues HTML de son jeu SEULEMENT. Une vue CANVAS de
 *   fiche est ÉTEINTE tant qu'une porte ne la cite pas : elle vit dans le code
 *   de l'app, et ne doit apparaître chez aucun client qui ne l'a pas activée
 *   au Studio. Une vue HTML, elle, est publiée par un admin de l'org : la
 *   publier vaut activation.
 * Une porte sans jeu n'en a aucune.
 *
 * LA SEULE IMPLÉMENTATION de cette règle : le schéma, l'assemblage du runtime
 * et le Studio la lisent ici (`docs/socle/fiche-suivi.md`, section 3).
 */
export function vuesDeFicheDeLaPorte(
  ecran: Pick<AppScreen, "portes" | "vuesCustom" | "vuesHtml">,
  porteId: string,
  options: { sansRestriction?: boolean } = {},
): string[] {
  const porte = ecran.portes.find((candidate) => candidate.id === porteId);
  const jeu = porte?.jeu;
  if (porte === undefined || jeu === undefined) return [];
  const memeJeu = (autre: { family: string; type?: string | undefined }): boolean =>
    autre.family === jeu.family && (autre.type ?? undefined) === (jeu.type ?? undefined);
  const canvas = (ecran.vuesCustom ?? [])
    .filter((vue) => vue.niveau === "record" && (vue.jeu === undefined || memeJeu(vue.jeu)))
    .map((vue) => vue.id)
    .filter((id) => id !== VUE_FICHE_DU_SOCLE);
  const html = (ecran.vuesHtml ?? [])
    .filter((vue) => memeJeu(vue.jeu))
    .map((vue) => vue.id)
    .filter((id) => id !== VUE_FICHE_DU_SOCLE);
  const offertes = [...canvas, ...html];
  if (options.sansRestriction === true) return offertes;
  if (porte.vuesFiche === undefined) return html;
  return porte.vuesFiche.filter((id) => offertes.includes(id));
}

/* -------------------------------------------------------------------------- */
/* Le fichier                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * LE FICHIER `taki.screens.json` d'une app, en entier.
 *
 * `version` est celle de la FORME du fichier, jamais celle de l'app : elle
 * monte quand une clef change de SENS ou de NOM, pas quand une clef facultative
 * arrive.
 */
const cibleSourceMigreeSchema = z.object({ ecran: z.string().min(1), porte: z.string().min(1), vue: z.string().min(1), role: z.string().min(1) }).strict();
export const appScreensFileSchema = z
  .object({
    version: z.literal(1),
    ecrans: z.array(appScreenSchema).min(1),
    compatibilite: z.object({
      sources: z.record(cibleSourceMigreeSchema.extend({ parPorte: z.record(cibleSourceMigreeSchema).optional() }).strict()).optional(),
      conditions: z.record(z.object({ ecran: z.string().min(1), porte: z.string().min(1), filtre: z.string().min(1) }).strict()).optional(),
    }).strict().optional(),
  })
  .strict()
  .superRefine((fichier, ctx) => {
    const vus = new Set<string>();
    for (const ecran of fichier.ecrans) {
      if (vus.has(ecran.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `l'écran "${ecran.id}" est déclaré deux fois`,
        });
      }
      vus.add(ecran.id);
    }
  });

/** Le contenu d'un `taki.screens.json`. */
export type AppScreensFile = z.infer<typeof appScreensFileSchema>;

/**
 * UNE INSTANCE NE DEPLACE PAS LES DONNEES D'UNE APP. Une porte du modele garde
 * sa famille dans l'instance qui la reprend (meme `id`) : la famille est le
 * lieu de stockage, que le modele fixe (`entities` ; les rules et les portes
 * d'acces en derivent). Une instance ecrite AVANT que le modele ne deplace un
 * jeu (To do, de `tasks` vers `todos`, `docs/socle/todo-base.md`) lirait sinon
 * l'ancienne famille : la phrase rendue dit pourquoi elle est ecartee, `null`
 * si rien ne bouge. Une porte AJOUTEE par l'org vise ce qu'elle veut.
 */
export function porteDeplacee(
  modele: { portes: ReadonlyArray<{ id: string; jeu?: { family: string } | undefined }> },
  instance: { portes: ReadonlyArray<{ id: string; jeu?: { family: string } | undefined }> },
): string | null {
  for (const porte of instance.portes) {
    const native = modele.portes.find((p) => p.id === porte.id);
    if (native?.jeu && porte.jeu && native.jeu.family !== porte.jeu.family) {
      return `La porte « ${porte.id} » de l'org vise la famille ${porte.jeu.family}, le modèle ${native.jeu.family} : instance à republier sur ${native.jeu.family}.`;
    }
  }
  return null;
}

/** Convertit seulement les anciens noms explicitement remplacés par le modèle, sans écrire en base. */
export function migrerEcransDeclares(instance: AppScreensFile, modele: AppScreensFile): AppScreensFile {
  if (!modele.compatibilite) return instance;
  const portes = modele.ecrans.flatMap(e => e.portes);
  const lireSource = (nom: string, cible: z.infer<typeof cibleSourceMigreeSchema>) => {
    const ecran = modele.ecrans.find(e => e.id === cible.ecran);
    const porte = ecran?.portes.find(p => p.id === cible.porte);
    const binding = (porte?.vb ?? ecran?.vb)?.[cible.vue];
    const source = binding?.[cible.role as keyof typeof binding];
    if (!source || typeof source !== 'object' || 'fn' in source || !appScreenSourceSchema.safeParse(source).success) throw new Error(`Migration de source introuvable : ${nom}`);
    return source as AppScreenSource;
  };
  const parcourir = (valeur: unknown, porte?: string): unknown => {
    if (Array.isArray(valeur)) return valeur.map(enfant => parcourir(enfant, porte));
    if (!valeur || typeof valeur !== 'object') return valeur;
    const objet = valeur as Record<string, unknown>;
    const fn = typeof objet.fn === 'string' ? objet.fn : '';
    const cibleSource = modele.compatibilite?.sources?.[fn];
    if (cibleSource) {
      const source = lireSource(fn, (porte && cibleSource.parPorte?.[porte]) || cibleSource);
      const { fn: _fn, ...options } = objet;
      const suite = { ...structuredClone(source), ...options };
      if (suite.modifiable && estSourceTransformee(suite)) suite.modifiable = false;
      return suite;
    }
    const cible = modele.compatibilite?.conditions?.[fn];
    if (cible) {
      const condition = modele.ecrans.find(e => e.id === cible.ecran)?.portes.find(p => p.id === cible.porte)?.filtres?.autres?.find(f => f.id === cible.filtre)?.condition;
      if (!condition) throw new Error(`Migration de condition introuvable : ${fn}`);
      const { fn: _fn, ...options } = objet;
      return { ...options, condition: structuredClone(condition) };
    }
    return Object.fromEntries(Object.entries(objet).map(([clef, enfant]) => [clef, clef === 'portes' && Array.isArray(enfant)
      ? enfant.map(p => parcourir(p, (portes.find(n => n.id === p.id) ?? portes.find(n => n.jeu && n.jeu.family === p.jeu?.family && n.jeu.type === p.jeu?.type))?.id ?? p.id))
      : parcourir(enfant, porte)]));
  };
  return lireEcransDeclares(parcourir(instance), 'compatibilité déclarative');
}

/**
 * LIRE UN `taki.screens.json`, et dire POURQUOI quand il est faux.
 *
 * Même raison que `lireChampsDeclares` : un `import` de JSON n'est PAS vérifié
 * par TypeScript, il élargit toute union littérale en `string`. Un
 * `"arrivee": "core.list"` compilerait en vert et rendrait une zone 5 vide. Ce
 * `safeParse` est la compensation exacte de ce trou, et il jette avec le chemin
 * fautif plutôt que de monter un écran à moitié construit.
 *
 * En CI, le même fichier est vérifié par `scripts/check-app-entities.mjs`, et
 * c'est LUI qui fait foi : ici on protège le poste de développement, là-bas on
 * protège `main`.
 */
export function lireEcransDeclares(brut: unknown, source: string): AppScreensFile {
  const lu = appScreensFileSchema.safeParse(brut);
  if (lu.success) return lu.data;
  const details = lu.error.issues
    .map((issue) => `${issue.path.join(".") || "(racine)"} : ${issue.message}`)
    .join(" | ");
  throw new Error(`Fichier d'écrans invalide (${source}) : ${details}`);
}

/**
 * UN ÉCRAN par son id, ou une exception qui le nomme.
 *
 * Rendre `undefined` obligerait chaque app à écrire le même garde ; et un écran
 * absent n'est pas un cas de figure, c'est une faute de déclaration qui doit se
 * voir au montage.
 */
/**
 * LE DÉTAIL PAR VUE D'UNE PORTE : celui de la porte, sinon celui de l'écran.
 *
 * C'EST LA RÈGLE DE RÉSOLUTION, ÉCRITE UNE FOIS. Elle est déclarée sur
 * `appScreenDoorSchema.vb` et implémentée ici, et nulle part ailleurs : la
 * refaire à la main dans une app la ferait diverger le jour où elle change.
 *
 * ELLE NE FUSIONNE PAS, et c'est délibéré : une porte qui déclare son détail
 * remplace celui de l'écran EN ENTIER pour elle seule. Mélanger le titre d'un
 * jeu aux colonnes d'un autre produirait une vue que personne n'a déclarée, et
 * un rôle qui cite un champ absent du jeu de la porte fait jeter l'assemblage
 * au montage.
 *
 * UNE PORTE INCONNUE REND LE DÉTAIL DE L'ÉCRAN : cette fonction résout, elle ne
 * valide pas. C'est `porteDeclaree` (dans `@taki/ui`) qui refuse un id de porte
 * absent, avec son nom, à l'endroit où l'assemblage le demande.
 */
export function detailParVue(
  ecran: AppScreen,
  porteId: string,
): Record<string, AppScreenBinding> {
  const porte = ecran.portes.find((p) => p.id === porteId);
  return porte?.vb ?? ecran.vb;
}

export function ecranDeclare(fichier: AppScreensFile, id: string): AppScreen {
  const ecran = fichier.ecrans.find((e) => e.id === id);
  if (ecran === undefined) {
    const connus = fichier.ecrans.map((e) => e.id).join(", ");
    throw new Error(`Écran "${id}" absent de la déclaration (écrans déclarés : ${connus}).`);
  }
  return ecran;
}
